import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import type { Sponsor, Supporter } from '@/types/sponsors';
import { fetchSponsors, fetchSupporters, sortByDisplayOrder } from '@/data/sponsors-api';

export default function Home() {
  const location = useLocation();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [isVisible, setIsVisible] = useState<Record<string, boolean>>({});
  const [showSponsorForm, setShowSponsorForm] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);
  const [flyerLightboxOpen, setFlyerLightboxOpen] = useState(false);
  const [flyerLightboxIndex, setFlyerLightboxIndex] = useState(0);
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [supporters, setSupporters] = useState<Supporter[]>([]);

  useEffect(() => {
    Promise.all([fetchSponsors(), fetchSupporters()]).then(([s, u]) => {
      setSponsors(s);
      setSupporters(u);
    });
  }, []);

  // ?open=contact でお問い合わせモーダルを開く（サブページからリンク時）
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('open') === 'contact') {
      setShowContactForm(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // #sponsorship 等でサブページから遷移した時に該当セクションへスクロール
  useEffect(() => {
    const hash = location.hash?.replace('#', '') || window.location.hash?.replace('#', '');
    if (!hash) return;
    const scrollToEl = () => {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    const t = setTimeout(scrollToEl, 150);
    return () => clearTimeout(t);
  }, [location.hash]);

  // -------------------------------------------------
  // IntersectionObserver – fade‑in animation handling
  // -------------------------------------------------
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({
              ...prev,
              [entry.target.id]: true,
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // -------------------------------------------------
  // Scroll detection – header background toggle
  // -------------------------------------------------

  // -------------------------------------------------
  // Flyer lightbox
  // -------------------------------------------------
  const flyers = [
    { src: '/images/S__42582143_0.jpg', alt: '国際女性デー2026 チラシ（表）', label: '表（おもて）' },
    { src: '/images/S__42582145_0.jpg', alt: '国際女性デー2026 チラシ（裏）', label: '裏' },
  ];
  const openFlyerLightbox = (index: number) => {
    setFlyerLightboxIndex(index);
    setFlyerLightboxOpen(true);
  };
  useEffect(() => {
    if (!flyerLightboxOpen) return;
    document.body.style.overflow = 'hidden';
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFlyerLightboxOpen(false);
      if (e.key === 'ArrowLeft') setFlyerLightboxIndex((i) => (i === 0 ? 1 : i - 1));
      if (e.key === 'ArrowRight') setFlyerLightboxIndex((i) => (i === 1 ? 0 : i + 1));
    };
    window.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [flyerLightboxOpen]);

  // -------------------------------------------------
  // UI helpers
  // -------------------------------------------------
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };


  // -------------------------------------------------
  // Form submitters – with robust error handling
  // -------------------------------------------------
  const submitForm = async (
    endpoint: string,
    formData: FormData,
    type: 'sponsor' | 'contact'
  ) => {
    try {
      // FormSubmit用の追加パラメータ（info@iwd-japanwomen.org に受信）
      formData.append('_subject', type === 'sponsor' ? '【協賛】国際女性デー2026 お問い合わせ' : '【お問い合わせ】国際女性デー2026');
      formData.append('_template', 'table');
      formData.append('_captcha', 'false');
      const submitterEmail = formData.get('メールアドレス') ?? formData.get('email');
      if (submitterEmail) {
        formData.append('_replyto', String(submitterEmail));
        // 送信者にCCでコピーを送り、受信確認として利用（_autoresponseはAJAX非対応のため）
        formData.append('_cc', String(submitterEmail));
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any),
      });

      if (!response.ok) {
        // Throw an error so the catch block below runs
        const errorText = await response.text();
        throw new Error(`Server responded ${response.status}: ${errorText}`);
      }

      // Success handling
      setFormSuccess(type);
      // Reset the form after a successful submit
      const formEl = document.getElementById(
        type === 'sponsor' ? 'sponsor-form' : 'contact-form'
      ) as HTMLFormElement | null;
      formEl?.reset();

      // Auto‑close the modal after a short period
      setTimeout(() => {
        if (type === 'sponsor') setShowSponsorForm(false);
        else setShowContactForm(false);
        setFormSuccess(null);
      }, 2000);
    } catch (error) {
      console.error('Form submission error:', error);
      alert('送信中にエラーが発生しました。しばらくしてから再度お試しください。');
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleSponsorFormSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();
    setFormSubmitting(true);
    const formData = new FormData(e.currentTarget);
    await submitForm(
      'https://formsubmit.co/ajax/info@iwd-japanwomen.org',
      formData,
      'sponsor'
    );
  };

  const handleContactFormSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();
    setFormSubmitting(true);
    const formData = new FormData(e.currentTarget);
    await submitForm(
      'https://formsubmit.co/ajax/info@iwd-japanwomen.org',
      formData,
      'contact'
    );
  };

  // -------------------------------------------------
  // Color palette – kept in a single object for easy reuse
  // -------------------------------------------------
  const colors = {
    coral: '#E8847C',
    coralLight: '#F5AEA8',
    mint: '#7ECEC1',
    mintLight: '#b8e4dd',
    cream: '#FFF9F5',
    warmGray: '#4A4543',
  };

  // -------------------------------------------------
  // JSX – the whole page layout
  // -------------------------------------------------
  return (
    <div
      className="min-h-screen bg-white overflow-x-hidden"
      style={{ fontFamily: "'Zen Maru Gothic', sans-serif" }}
    >
      <Header variant="home" />


      {/* -------------------------------------------------
          Hero section – big visual + CTA
      ------------------------------------------------- */}
      <section
        className="relative min-h-screen pt-24 pb-20 overflow-hidden"
        style={{ backgroundColor: colors.cream }}
      >
        {/* Background shapes - enhanced depth */}
        <div className="absolute inset-0">
          {/* Base gradient layers */}
          <div className="absolute inset-0 bg-gradient-to-br from-white via-transparent to-transparent opacity-80"></div>
          <div className="absolute inset-0 bg-gradient-to-tl from-transparent via-white/30 to-white/60"></div>
          
          {/* Large blurred orbs - back layer（ぼかし変化・じわっと動く）*/}
          <div
            className="absolute -top-60 -right-60 w-[800px] h-[800px] rounded-full opacity-30"
            style={{
              background: `radial-gradient(circle, ${colors.coralLight} 0%, transparent 60%)`,
              animation: 'heroOrbBlur 14s ease-in-out infinite, heroOrbDrift 18s ease-in-out infinite',
            }}
          ></div>
          <div
            className="absolute top-1/4 -left-60 w-[700px] h-[700px] rounded-full opacity-25"
            style={{
              background: `radial-gradient(circle, ${colors.mintLight} 0%, transparent 60%)`,
              animation: 'heroOrbBlur 16s ease-in-out infinite reverse, heroOrbDrift 22s ease-in-out infinite 2s',
            }}
          ></div>
          <div
            className="absolute -bottom-40 right-1/3 w-[600px] h-[600px] rounded-full opacity-20"
            style={{
              background: `radial-gradient(circle, ${colors.coralLight} 0%, transparent 60%)`,
              animation: 'heroOrbBlur 12s ease-in-out infinite 1s, heroOrbDrift 20s ease-in-out infinite 4s',
            }}
          ></div>

          {/* Medium orbs - middle layer */}
          <div
            className="absolute top-20 right-1/3 w-48 h-48 rounded-full opacity-25"
            style={{ 
              backgroundColor: colors.mint,
              animation: 'heroOrbBlurMid 9s ease-in-out infinite, heroOrbDrift 11s ease-in-out infinite 1s',
            }}
          ></div>
          <div
            className="absolute bottom-32 left-1/3 w-40 h-40 rounded-full opacity-20"
            style={{ 
              backgroundColor: colors.coral,
              animation: 'heroOrbBlurMid 10s ease-in-out infinite reverse 0.5s, heroOrbDrift 13s ease-in-out infinite 3s',
            }}
          ></div>
          <div
            className="absolute top-1/2 right-10 w-32 h-32 rounded-full opacity-25"
            style={{ 
              backgroundColor: colors.mintLight,
              animation: 'heroOrbBlurMid 8s ease-in-out infinite 1s, heroOrbDrift 9s ease-in-out infinite',
            }}
          ></div>

          {/* Small decorative circles - front layer（じわっと動く）*/}
          <div
            className="absolute top-32 right-1/4 w-32 h-32 rounded-full opacity-30 shadow-lg"
            style={{ 
              backgroundColor: colors.mint,
              boxShadow: `0 20px 60px ${colors.mint}40`,
              animation: 'heroOrbDrift 12s ease-in-out infinite',
            }}
          ></div>
          <div
            className="absolute bottom-40 left-1/4 w-24 h-24 rounded-full opacity-25 shadow-lg"
            style={{ 
              backgroundColor: colors.coral,
              boxShadow: `0 15px 50px ${colors.coral}40`,
              animation: 'heroOrbDrift 15s ease-in-out infinite 2s',
            }}
          ></div>
          <div
            className="absolute top-1/2 right-20 w-16 h-16 rounded-full opacity-30 shadow-md"
            style={{ 
              backgroundColor: colors.mintLight,
              boxShadow: `0 10px 30px ${colors.mintLight}50`,
              animation: 'heroOrbDrift 10s ease-in-out infinite 1s',
            }}
          ></div>
          <div
            className="absolute top-40 left-20 w-12 h-12 rounded-full opacity-20"
            style={{ 
              backgroundColor: colors.coralLight,
              boxShadow: `0 8px 25px ${colors.coralLight}40`,
              animation: 'heroOrbDrift 14s ease-in-out infinite 0.5s',
            }}
          ></div>
          <div
            className="absolute bottom-60 right-1/4 w-20 h-20 rounded-full opacity-15"
            style={{ 
              backgroundColor: colors.mint,
              boxShadow: `0 12px 35px ${colors.mint}30`,
              animation: 'heroOrbDrift 11s ease-in-out infinite 3s',
            }}
          ></div>
        </div>

        {/* Floating particles - 大きさ・間隔・ぼかしをランダムに */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[
            { size: 2, left: '8%', color: colors.coral, dur: 28, delay: 0, blur: 0, opacity: 0.5 },
            { size: 6, left: '15%', color: colors.mint, dur: 22, delay: 7, blur: 4, opacity: 0.35 },
            { size: 4, left: '25%', color: colors.coralLight, dur: 32, delay: 2, blur: 0, opacity: 0.4 },
            { size: 3, left: '45%', color: colors.mintLight, dur: 26, delay: 12, blur: 2, opacity: 0.45 },
            { size: 8, left: '55%', color: colors.coral, dur: 20, delay: 4, blur: 6, opacity: 0.3 },
            { size: 5, left: '70%', color: colors.mint, dur: 24, delay: 9, blur: 3, opacity: 0.38 },
            { size: 2, left: '82%', color: colors.coralLight, dur: 30, delay: 15, blur: 0, opacity: 0.5 },
            { size: 7, left: '35%', color: colors.mintLight, dur: 18, delay: 5, blur: 5, opacity: 0.32 },
            { size: 4, left: '90%', color: colors.coral, dur: 25, delay: 11, blur: 0, opacity: 0.42 },
            { size: 3, left: '62%', color: colors.mint, dur: 27, delay: 3, blur: 2, opacity: 0.48 },
          ].map((p, i) => (
            <div
              key={i}
              className="absolute rounded-full"
              style={{
                width: p.size,
                height: p.size,
                left: p.left,
                bottom: '-10px',
                backgroundColor: p.color,
                opacity: p.opacity,
                filter: p.blur ? `blur(${p.blur}px)` : undefined,
                animation: `heroFloatUp ${p.dur}s linear infinite`,
                animationDelay: `${p.delay}s`,
              }}
            />
          ))}
        </div>

        {/* Main hero content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-h-[calc(100vh-200px)]">
            {/* Text side */}
            <div className="order-2 lg:order-1">
              {/* Date badge */}
              <div
                className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-white via-white to-white/90 backdrop-blur-sm shadow-lg mb-10 animate-bounce-slow"
                style={{
                  borderRadius: '100px',
                  border: `2px solid ${colors.coralLight}`,
                  boxShadow: `0 10px 40px ${colors.coral}20`,
                }}
              >
                <span className="relative flex h-3 w-3">
                  <span
                    className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                    style={{ backgroundColor: colors.coral }}
                  ></span>
                  <span
                    className="relative inline-flex rounded-full h-3 w-3"
                    style={{ backgroundColor: colors.coral }}
                  ></span>
                </span>
                <span
                  className="font-bold tracking-wide flex items-baseline gap-1"
                  style={{ color: colors.coral }}
                >
                  <span className="text-4xl font-black" style={{ fontFamily: "'Shippori Mincho', serif" }}>
                    2026
                  </span>
                  <span className="text-base">年</span>
                  <span className="text-4xl font-black" style={{ fontFamily: "'Shippori Mincho', serif" }}>
                    3
                  </span>
                  <span className="text-base">月</span>
                  <span className="text-4xl font-black" style={{ fontFamily: "'Shippori Mincho', serif" }}>
                    14
                  </span>
                  <span className="text-base">日（土）開催</span>
                </span>
              </div>

              {/* Main heading */}
              <h1 className="mb-8" style={{ fontFamily: "'Shippori Mincho', serif" }}>
                <span
                  className="block text-6xl sm:text-7xl lg:text-8xl font-black leading-none tracking-tight mb-2 drop-shadow-sm"
                  style={{ color: colors.warmGray }}
                >
                  愛と希望の
                </span>
                <span
                  className="block text-7xl sm:text-8xl lg:text-9xl font-black leading-none tracking-tight drop-shadow-lg"
                  style={{ 
                    color: colors.coral,
                    textShadow: `0 4px 20px ${colors.coral}40, 0 8px 40px ${colors.coral}20`
                  }}
                >
                  翼で
                </span>
                <span
                  className="block text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight mt-4"
                  style={{ color: colors.warmGray }}
                >
                  世界を変える
                </span>
              </h1>

              {/* Sub‑copy */}
              <p className="text-2xl sm:text-3xl font-bold mb-4" style={{ color: colors.warmGray }}>
                国際女性デー チャリティイベント
              </p>
              <p className="text-lg sm:text-xl text-gray-500 mb-10 leading-relaxed max-w-lg">
                全ての女性が健康で生きやすく、<br />
                活躍できる社会を目指して
              </p>

              {/* Info cards */}
              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <div
                  className="flex items-center gap-3 px-5 py-3 bg-white/80 backdrop-blur-sm shadow-md hover:shadow-xl transition-shadow"
                  style={{ 
                    borderRadius: '16px',
                    boxShadow: `0 8px 30px rgba(0,0,0,0.08)`,
                  }}
                >
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-xl"
                    style={{ backgroundColor: colors.mintLight }}
                  >
                    <i className="ri-calendar-line text-xl" style={{ color: colors.mint }}></i>
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">3月14日（土）</div>
                    <div className="text-xs text-gray-500">13:30-18:00</div>
                  </div>
                </div>

                <div
                  className="flex items-center gap-3 px-5 py-3 bg-white/80 backdrop-blur-sm shadow-md hover:shadow-xl transition-shadow"
                  style={{ 
                    borderRadius: '16px',
                    boxShadow: `0 8px 30px rgba(0,0,0,0.08)`,
                  }}
                >
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-xl"
                    style={{ backgroundColor: `${colors.coralLight}40` }}
                  >
                    <i className="ri-map-pin-line text-xl" style={{ color: colors.coral }}></i>
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">TODAホール</div>
                    <div className="text-xs text-gray-500">東京都中央区京橋</div>
                  </div>
                </div>
              </div>

              {/* Primary CTA */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
                <a
                  href="https://peatix.com/event/4805808/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-3 px-6 py-3 text-xl font-bold text-white whitespace-nowrap cursor-pointer rounded-xl bg-[#7ECEC1] hover:bg-[#6ebdb0] transition-colors"
                >
                  <span>参加チケットを購入</span>
                  <i className="ri-arrow-right-line text-2xl group-hover:translate-x-1 transition-transform"></i>
                </a>

                <div className="flex items-center gap-2 text-gray-500">
                  <i className="ri-ticket-line text-xl" style={{ color: colors.coral }}></i>
                  <span className="flex items-center gap-2 text-base font-medium">
                    参加費 <strong className="text-gray-900 text-2xl">¥10,000</strong>
                  </span>
                </div>
              </div>
            </div>

            {/* Image side - enhanced depth */}
            <div className="order-1 lg:order-2">
              <div className="relative">
                {/* Back decorative layer */}
                <div
                  className="absolute -top-12 -right-12 w-full h-full rounded-3xl"
                  style={{
                    background: `linear-gradient(135deg, ${colors.mintLight}40 0%, ${colors.coralLight}30 100%)`,
                    transform: 'rotate(6deg) scale(1.05)',
                    filter: 'blur(2px)',
                  }}
                ></div>
                
                {/* Middle decorative layer */}
                <div
                  className="absolute -top-8 -right-8 w-full h-full rounded-3xl"
                  style={{
                    background: `linear-gradient(135deg, ${colors.mintLight}60 0%, ${colors.coralLight}40 100%)`,
                    transform: 'rotate(3deg)',
                    boxShadow: `0 20px 60px ${colors.mint}30`,
                  }}
                ></div>
                
                {/* Decorative shapes */}
                <div
                  className="absolute -bottom-8 -left-8 w-32 h-32 rounded-2xl opacity-90"
                  style={{ 
                    backgroundColor: colors.mint,
                    boxShadow: `0 15px 40px ${colors.mint}50`,
                    transform: 'rotate(-5deg)',
                  }}
                ></div>
                <div
                  className="absolute -top-6 right-12 w-20 h-20 rounded-xl opacity-70"
                  style={{ 
                    backgroundColor: colors.coralLight,
                    boxShadow: `0 10px 30px ${colors.coral}40`,
                    transform: 'rotate(10deg)',
                  }}
                ></div>
                <div
                  className="absolute top-1/2 -left-4 w-12 h-12 rounded-lg opacity-60"
                  style={{ 
                    backgroundColor: colors.mintLight,
                    boxShadow: `0 8px 25px ${colors.mint}30`,
                  }}
                ></div>
                <div
                  className="absolute bottom-1/4 -right-4 w-16 h-16 rounded-xl opacity-50"
                  style={{ 
                    backgroundColor: colors.coralLight,
                    boxShadow: `0 10px 30px ${colors.coral}30`,
                    transform: 'rotate(-8deg)',
                  }}
                ></div>

                {/* Image container */}
                <div 
                  className="relative rounded-3xl overflow-hidden"
                  style={{
                    boxShadow: `0 25px 80px rgba(0,0,0,0.15), 0 10px 30px ${colors.coral}20`,
                  }}
                >
                  <img
                    src="/images/S__42582143_0.jpg"
                    alt="国際女性デー チャリティイベント ポスター"
                    className="w-full h-auto"
                  />
                  {/* Subtle overlay for depth */}
                  <div 
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: 'linear-gradient(135deg, transparent 60%, rgba(255,255,255,0.1) 100%)',
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CSS animations - ヒーローエフェクト用 */}
        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
          @keyframes floatUp {
            0% { transform: translateY(100vh) rotate(0deg); opacity: 0; }
            10% { opacity: 0.5; }
            90% { opacity: 0.5; }
            100% { transform: translateY(-100vh) rotate(360deg); opacity: 0; }
          }
          @keyframes pulse {
            0%, 100% { opacity: 0.6; }
            50% { opacity: 0.8; }
          }
          /* 背景オブジェクト: ぼかしがじわっと変化 */
          @keyframes heroOrbBlur {
            0%, 100% { filter: blur(100px); opacity: 0.25; }
            50% { filter: blur(150px); opacity: 0.35; }
          }
          @keyframes heroOrbBlurMid {
            0%, 100% { filter: blur(45px); opacity: 0.22; }
            50% { filter: blur(75px); opacity: 0.3; }
          }
          /* 背景オブジェクト: じわっと漂う動き */
          @keyframes heroOrbDrift {
            0%, 100% { transform: translate(0, 0); }
            25% { transform: translate(15px, -12px); }
            50% { transform: translate(-8px, -20px); }
            75% { transform: translate(-18px, -8px); }
          }
          /* 浮遊パーティクル: 下から上、サイズ・透明度も変化 */
          @keyframes heroFloatUp {
            0% { transform: translateY(100vh) scale(0.6); opacity: 0; }
            8% { opacity: 0.6; }
            50% { transform: translateY(0) scale(1.1); opacity: 0.5; }
            92% { opacity: 0.5; }
            100% { transform: translateY(-100vh) scale(0.8); opacity: 0; }
          }
        `}</style>
      </section>

      {/* -------------------------------------------------
          Benefits – Why join us
      ------------------------------------------------- */}
      <section
        id="benefits"
        data-animate
        className="py-28 bg-white relative overflow-hidden"
      >
        <div
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative transition-all duration-1000 ${
            isVisible['benefits'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-20">
            <span
              className="inline-block px-4 py-1.5 text-xs font-bold tracking-widest uppercase mb-6"
              style={{
                backgroundColor: `${colors.mintLight}60`,
                color: colors.mint,
                borderRadius: '100px',
              }}
            >
              Why Join Us
            </span>
            <h2
              className="text-5xl sm:text-6xl font-black mb-6"
              style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
            >
              あなたの一日が、未来につながる
            </h2>
            <p className="text-xl text-gray-500 max-w-2xl mx-auto">
              自分の未来と誰かの未来が重なる、特別な時間
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 items-stretch">
            {[
              {
                icon: 'ri-book-open-line',
                label: '学び',
                title: '「女性の人生を軽やかにする最前線に出会う」',
                desc: 'ホルモン、体調、働き方、人生の選択。第一線の専門家の経験と知見から、今の自分に必要なヒントが見つかります。知識だけでなく、『これからの生き方』を持ち帰る時間です。',
                color: colors.coral,
              },
              {
                icon: 'ri-palette-line',
                label: '体験',
                title: '「愛と希望を＊体験＊として持ち帰る」',
                desc: '美しいランウエイ、心が動く講演、アート、対話、交流。知識だけでなく、感動や出会いを持ち帰れる、ここでしか味わえない特別な一日。',
                color: colors.mint,
              },
              {
                icon: 'ri-heart-line',
                label: '社会貢献',
                title: '「この一枚のチケットが未来を動かす」',
                desc: '本イベントの収益は女性の健康・支援活動へ活用されます。参加すること自体が社会を支える行動となり、自分の学びや出会いが、誰かの未来につながるチャリティーイベントです。',
                color: colors.coral,
              },
            ].map((item, index) => (
              <div key={index} className="group relative flex">
                <div
                  className="relative bg-white p-10 transition-all duration-500 hover:-translate-y-2 flex flex-col w-full h-full min-h-full"
                  style={{
                    borderRadius: '24px',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                    border: '1px solid #f0f0f0',
                  }}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div
                      className="w-16 h-16 flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110"
                      style={{ backgroundColor: `${item.color}15`, borderRadius: '16px' }}
                    >
                      <i className={`${item.icon} text-3xl`} style={{ color: item.color }}></i>
                    </div>
                    <span
                      className="text-2xl font-bold"
                      style={{ color: item.color }}
                    >
                      {item.label}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold mb-4" style={{ color: colors.warmGray }}>
                    {item.title}
                  </h3>
                  <p className="text-gray-500 leading-relaxed text-base">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* -------------------------------------------------
          Purpose – Event mission
      ------------------------------------------------- */}
      <section
        id="purpose"
        data-animate
        className="py-28 relative overflow-hidden"
        style={{ backgroundColor: colors.cream }}
      >
        <div
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative transition-all duration-1000 ${
            isVisible['purpose'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="relative">
              <div
                className="absolute -top-6 -left-6 w-full h-full rounded-3xl"
                style={{ backgroundColor: `${colors.mintLight}50` }}
              ></div>
              <div className="relative">
                <div className="w-full h-[450px] rounded-3xl overflow-hidden shadow-xl">
                  <img
                    src="/images/event.jpg"
                    alt="女性の健康と活躍"
                    className="w-full h-full object-cover object-top" 
                  />
                </div>
                <p className="text-center text-gray-500 text-sm">昨年度（２０２５年３月８日開催）のイベントの様子</p>
                <p className="text-center text-gray-500 text-sm">昨年参加者に推進リーダー認定証発行</p>
              </div>
            </div>

            {/* Text */}
            <div>
              <span
                className="inline-block px-4 py-1.5 text-xs font-bold tracking-widest uppercase mb-6"
                style={{
                  backgroundColor: `${colors.coralLight}40`,
                  color: colors.coral,
                  borderRadius: '100px',
                }}
              >
                Our Mission
              </span>
              <h2
                className="text-5xl sm:text-6xl font-black mb-8 leading-tight"
                style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
              >
                開催趣旨
              </h2>

              <div className="space-y-6">
                <p className="text-gray-500 leading-relaxed text-lg">
                  主催: 日本女性財団　国際女性デー実行委員会
                </p>                
                <p className="font-bold text-2xl" style={{ color: colors.coral }}>
                  国際女性デー チャリティーイベント ”愛と希望の翼で世界を変える”
                </p>
                <p className="text-gray-500 leading-relaxed text-lg">
                  【開催趣旨：⼥性の健康課題を「社会の課題」へ】
                </p>
                <p className="text-gray-500 leading-relaxed text-lg">
                  3⽉8⽇の国際⼥性デー（※本イベントの開催日は3月14日です） は、ジェンダー平等と⼥性のエンパワーメントを推進する世界的な記念⽇です。現在、⽇本のビジネスシーンにおいて「⼈的資本経営」の重要性が⾼まる中、更年期をはじめとする⼥性特有の健康課題は、もはや個⼈の問題ではなく、企業の持続的な成⻑を左右する重要な経営課題となっています。
                </p>
                <p className="text-gray-500 leading-relaxed text-lg">
                  ⽇本⼥性財団は、すべての⼥性がライフステージに関わらず⾃分らしく⼒を発揮できる社会を⽬指しています。本イベントは、国際⼥性デーを機に、企業・個⼈・専⾨家が「DE&I（多様性・公平性・包摂）」の精神で繋がり、誰もが輝ける未来を共創するためのチャリティイベントとして開催いたします。
                </p>
              </div>

              {/* Tag line */}
              <div className="mt-10 flex flex-wrap gap-3">
                {[
                  { icon: 'ri-heart-pulse-line', label: '健康支援' },
                  { icon: 'ri-team-line', label: 'コミュニティ' },
                  { icon: 'ri-lightbulb-line', label: '啓発活動' },
                ].map((tag, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 px-4 py-2 bg-white shadow-sm"
                    style={{ borderRadius: '100px' }}
                  >
                    <i className={`${tag.icon} text-lg`} style={{ color: colors.mint }}></i>
                    <span className="text-base font-medium text-gray-600">{tag.label}</span>
                  </div>
                ))}
              </div>

              <div className="mt-10 mb-8 px-5 py-4 rounded-2xl" style={{ backgroundColor: colors.cream }}>
                <p className="text-lg font-bold mb-1" style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}>
                  楽しみながら社会に還元する一日
                </p>
                <p className="text-base text-gray-600">学び、文化、出会いがひとつになる時間をお届けします</p>
              </div>

              <div className="space-y-4 mb-8">
                {[
                  { icon: 'ri-chat-3-line', title: 'トークセッション', desc: '女性の健康と未来を考える対話の時間。', color: colors.coral },
                  { icon: 'ri-flower-line', title: 'キモノビューティーショー', desc: '日本の美と女性の凛とした姿を表現する特別演出。', color: colors.mint },
                  { icon: 'ri-hand-heart-line', title: 'チャリティーオークション', desc: '女性作家によるアート・日本工芸品を出展。参加すること自体が社会への還元につながります。', color: colors.coral },
                  { icon: 'ri-gift-line', title: '企業協賛抽選会', desc: '1万円以上の協賛品も含まれる特別なギフト。', color: colors.mint },
                  { icon: 'ri-film-line', title: '昨年の感動をもう一度', desc: 'ショートムービーで昨年の空気感をご覧いただけます。', color: colors.coral },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 items-start">
                    <div
                      className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl text-white"
                      style={{ backgroundColor: item.color }}
                    >
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <div>
                      <p className="font-bold text-gray-800">{item.title}</p>
                      <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* イベントが届けたいこと – 今年のプログラムを象徴するバナーリンク */}
              <div className="mt-12 -mx-4 sm:-mx-6 lg:-mx-8">
                <Link
                  to="/event"
                  className="group block relative overflow-hidden rounded-2xl shadow-2xl transition-all duration-500 hover:shadow-[0_30px_60px_rgba(74,69,67,0.25)] hover:scale-[1.02] active:scale-[0.99]"
                >
                  <div className="relative flex min-h-[300px] sm:min-h-[340px]">
                    {/* メイン背景：2026年オープニングアクト「響喜炎舞 源氏物語より」 */}
                    <div className="absolute inset-0 flex">
                      <div className="flex-1 relative min-w-0">
                        <img
                          src="/images/opening-act-eyecatch.png"
                          alt=""
                          className="w-full h-full object-cover object-center"
                        />
                      </div>
                      {/* 右側：シームレスギャラリー（キモノショー・基調講演） */}
                      <div className="hidden lg:flex w-1/3 flex-shrink-0">
                        <div className="flex-1 flex flex-col">
                          <div className="flex-1 min-h-0 overflow-hidden">
                            <img src="/images/kimono-beauty-japan.png" alt="" className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1 min-h-0 overflow-hidden">
                            <img src="/images/tsushima.jpg" alt="" className="w-full h-full object-cover object-top" />
                          </div>
                        </div>
                      </div>
                      {/* 右→左へ淡くなるオーバーレイ（右が濃い・左が透明／人物を潰さないようカラー・透過高め） */}
                      <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          background: 'linear-gradient(to left, rgba(95, 60, 55, 0.5) 0%, rgba(95, 62, 58, 0.35) 50%, transparent 100%)',
                        }}
                      />
                    </div>
                    {/* テキスト・CTA */}
                    <div className="relative z-10 flex flex-1 flex-col justify-center px-8 py-10 sm:px-12 sm:py-14 max-w-2xl">
                      <div className="relative z-10">
                        <span
                          className="inline-block px-4 py-1.5 text-xs font-bold tracking-widest uppercase mb-4 text-white"
                          style={{ backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '100px' }}
                        >
                          2026年3月14日開催
                        </span>
                        <h3
                          className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight mb-4 group-hover:translate-x-1 transition-transform duration-300"
                          style={{ fontFamily: "'Shippori Mincho', serif", textShadow: '0 2px 12px rgba(0,0,0,0.5), 0 4px 24px rgba(0,0,0,0.3)' }}
                        >
                          このイベントが届けたいこと
                        </h3>
                        <p className="text-base sm:text-lg text-white mb-6" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.5)' }}>
                          響喜炎舞・基調講演・キモノショー・チャリティオークション…<br className="hidden sm:block" />
                          最高に価値のある1日を、一緒に過ごしませんか。
                        </p>
                        <span
                          className="inline-flex items-center gap-3 py-4 px-8 rounded-xl font-bold text-white w-fit transition-all duration-300 group-hover:gap-5"
                          style={{ backgroundColor: colors.coral, boxShadow: '0 4px 20px rgba(232,132,124,0.5)' }}
                        >
                          詳しく読む
                          <i className="ri-arrow-right-line text-xl" />
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* -------------------------------------------------
          Program – timeline
      ------------------------------------------------- */}
      <section
        id="program"
        data-animate
        className="py-28 bg-white relative overflow-hidden"
      >
        <div
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${
            isVisible['program'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-20">
            <span
              className="inline-block px-4 py-1.5 text-xs font-bold tracking-widest uppercase mb-6"
              style={{
                backgroundColor: `${colors.mintLight}60`,
                color: colors.mint,
                borderRadius: '100px',
              }}
            >
              Program
            </span>
            <h2
              className="text-5xl sm:text-6xl font-black mb-6"
              style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
            >
              プログラム
            </h2>
            <p className="text-xl text-gray-500">学びと体験、交流が融合した充実の2部構成</p>
          </div>

          {/* Part 1 */}
          <div className="mb-20">
            <div className="flex items-center gap-4 mb-10">
              <div
                className="w-16 h-16 flex items-center justify-center text-white font-black text-2xl"
                style={{ backgroundColor: colors.coral, borderRadius: '16px' }}
              >
                1
              </div>
              <div>
                <div className="text-4xl font-black" style={{ color: colors.warmGray }}>
                  第1部
                </div>
                <div className="text-lg text-gray-500">13:30-15:10</div>
              </div>
            </div>

            <div className="relative pl-8 border-l-2" style={{ borderColor: colors.coralLight }}>
              <div className="space-y-6">
                {[
                  {
                    time: '13:30',
                    title: 'オープニングアクト',
                    items: ['チェロ演奏・バレエ 源氏物語', 'アート作品展示＆ウェルカムドリンク'],
                  },
                  {
                    time: '13:50',
                    title: 'キーノートスピーチ',
                    subsections: [
                      {
                        label: '基調講演',
                        items: ['「女性ホルモンで世界一幸せになる」医師 対馬 ルリ子'],
                      },
                      {
                        label: 'フェムシップドクター講演',
                        items: ['「女性と女の子の味方！フェムシップドクターズって何？」医師 髙宮城尚子、中島由美子'],
                      },
                    ],
                  },
                  {
                    time: '14:05',
                    title: 'トークセッション',
                    items: [
                      '「愛と希望で人生を切り拓く方法」',
                      '医師 富永喜代',
                      '植物療法士 森田敦子',
                      'ソーシャルファシリテーター 中野裕弓',
                      '※本トークセッションには、基調講演の対馬ルリ子医師も参加します',
                    ],
                  },
                  { time: '15:00', title: '写真撮影', items: [] },
                  { time: '15:10', title: '休憩', items: [], isBreak: true },
                ].map((item, index) => (
                  <div key={index} className="relative">
                    <div
                      className="absolute -left-[41px] w-4 h-4 rounded-full border-4 border-white"
                      style={{ backgroundColor: item.isBreak ? '#d1d5db' : colors.coral }}
                    ></div>
                    <div
                      className={`p-6 transition-all hover:shadow-lg ${item.isBreak ? 'bg-gray-50' : 'bg-white'}`}
                      style={{ borderRadius: '16px', border: '1px solid #f0f0f0' }}
                    >
                      <div className="flex flex-wrap items-start gap-4">
                        <div
                          className="px-4 py-2 bg-white shadow-sm font-bold text-xl"
                          style={{
                            borderRadius: '12px',
                            color: item.isBreak ? '#6b7280' : colors.coral,
                          }}
                        >
                          {item.time}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold mb-2" style={{ color: colors.warmGray }}>
                            {item.title}
                          </h3>
                          {'subsections' in item && (item as { subsections?: { label: string; items: string[] }[] }).subsections ? (
                            <div className="space-y-5">
                              {(item as { subsections: { label: string; items: string[] }[] }).subsections.map((sub, si) => (
                                <div key={si}>
                                  <div
                                    className="inline-block text-sm font-bold mb-2 px-3 py-1 rounded-lg"
                                    style={{ backgroundColor: `${colors.coralLight}60`, color: colors.coral }}
                                  >
                                    {sub.label}
                                  </div>
                                  <div className="space-y-1">
                                    {sub.items.map((text, i) => (
                                      <p key={i} className="text-gray-500 text-base">
                                        {text}
                                      </p>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : 'items' in item && item.items && item.items.length > 0 ? (
                            <div className="space-y-1">
                              {item.items.map((text: string, i: number) => (
                                <p key={i} className="text-gray-500 text-base">
                                  {text}
                                </p>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Part 2 */}
          <div>
            <div className="flex items-center gap-4 mb-10">
              <div
                className="w-16 h-16 flex items-center justify-center text-white font-black text-2xl"
                style={{ backgroundColor: colors.mint, borderRadius: '16px' }}
              >
                2
              </div>
              <div>
                <div className="text-4xl font-black" style={{ color: colors.warmGray }}>
                  第2部
                </div>
                <div className="text-lg text-gray-500">15:40-18:00</div>
              </div>
            </div>

            <div className="relative pl-8 border-l-2" style={{ borderColor: colors.mintLight }}>
              <div className="space-y-6">
                {[
                  {
                    time: '15:40',
                    title: 'インターミッション・アクト（軽食＆ドリンクを提供（ケータリング））',
                    items: [
                      'キモノビューティーショー',
                      'パリ・オペラ座やドバイで絶賛された日本のキモノビューティのランウェイショー',
                    ],
                  },
                  {
                    time: '16:10',
                    title: 'チャリティ・アートオークション',
                    items: [
                      '寄付を目的としたアート作品のオークション',
                      'アートからお楽しみ券まで。',
                      '初めての方も歓迎。',
                    ],
                  },
                  { time: '16:50', title: '女性のWell-Being応援 チャリティ抽選会', items: [] },
                  { time: '17:20', title: 'クロージングセレモニー', items: ['クロージング挨拶'] },
                  { time: '17:30', title: 'フリー＆交流タイム', items: [] },
                  { time: '18:00', title: '終了', items: [], isBreak: true },
                ].map((item, index) => (
                  <div key={index} className="relative">
                    <div
                      className="absolute -left-[41px] w-4 h-4 rounded-full border-4 border-white"
                      style={{ backgroundColor: item.isBreak ? '#d1d5db' : colors.mint }}
                    ></div>
                    <div
                      className={`p-6 transition-all hover:shadow-lg ${item.isBreak ? 'bg-gray-50' : 'bg-white'}`}
                      style={{ borderRadius: '16px', border: '1px solid #f0f0f0' }}
                    >
                      <div className="flex flex-wrap items-start gap-4">
                        <div
                          className="px-4 py-2 bg-white shadow-sm font-bold text-xl"
                          style={{
                            borderRadius: '12px',
                            color: item.isBreak ? '#6b7280' : colors.mint,
                          }}
                        >
                          {item.time}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold mb-2" style={{ color: colors.warmGray }}>
                            {item.title}
                          </h3>
                          {'subsections' in item && (item as { subsections?: { label: string; items: string[] }[] }).subsections ? (
                            <div className="space-y-5">
                              {(item as { subsections: { label: string; items: string[] }[] }).subsections.map((sub, si) => (
                                <div key={si}>
                                  <div
                                    className="inline-block text-sm font-bold mb-2 px-3 py-1 rounded-lg"
                                    style={{ backgroundColor: `${colors.coralLight}60`, color: colors.coral }}
                                  >
                                    {sub.label}
                                  </div>
                                  <div className="space-y-1">
                                    {sub.items.map((text, i) => (
                                      <p key={i} className="text-gray-500 text-base">
                                        {text}
                                      </p>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : 'items' in item && item.items && item.items.length > 0 ? (
                            <div className="space-y-1">
                              {item.items.map((text: string, i: number) => (
                                <p key={i} className="text-gray-500 text-base">
                                  {text}
                                </p>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Dress code CTA */}
          <div
            className="mt-16 p-8 flex flex-col sm:flex-row items-center justify-between gap-6"
            style={{ backgroundColor: colors.cream, borderRadius: '24px' }}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-14 h-14 flex items-center justify-center"
                style={{ backgroundColor: colors.coral, borderRadius: '14px' }}
              >
                <i className="ri-shirt-line text-2xl text-white"></i>
              </div>
              <div>
                <div className="font-bold text-xl" style={{ color: colors.warmGray }}>
                  ドレスコード
                </div>
                <div className="text-gray-500 text-base">愛と希望の翼（自由なスタイルで参加歓迎）</div>
              </div>
            </div>
            <a
              href="https://peatix.com/event/4805808/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 text-lg font-bold text-white whitespace-nowrap cursor-pointer rounded-xl bg-[#7ECEC1] hover:bg-[#6ebdb0] transition-colors"
            >
              <span>プログラムに参加する</span>
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* -------------------------------------------------
          Speakers
      ------------------------------------------------- */}
      <section
        id="speakers"
        data-animate
        className="py-28 relative overflow-hidden"
        style={{ backgroundColor: colors.cream }}
      >
        <div
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative transition-all duration-1000 ${
            isVisible['speakers'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-20">
            <span
              className="inline-block px-4 py-1.5 text-xs font-bold tracking-widest uppercase mb-6"
              style={{
                backgroundColor: `${colors.coralLight}40`,
                color: colors.coral,
                borderRadius: '100px',
              }}
            >
              Speakers
            </span>
            <h2
              className="text-5xl sm:text-6xl font-black mb-6"
              style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
            >
              登壇者紹介
            </h2>
            <p className="text-xl text-gray-500">女性の健康を支える第一線の専門家たち</p>
          </div>

          {/* Keynote speaker */}
          <div className="mb-20">
            <div className="flex items-center gap-3 mb-10">
              <div
                className="w-12 h-12 flex items-center justify-center"
                style={{ backgroundColor: colors.coral, borderRadius: '12px' }}
              >
                <i className="ri-mic-line text-xl text-white"></i>
              </div>
              <h3 className="text-3xl font-black" style={{ color: colors.warmGray }}>
                基調講演
              </h3>
            </div>

            <div
              className="relative overflow-hidden"
              style={{
                background: `linear-gradient(135deg, ${colors.coral} 0%, #D4736B 100%)`,
                borderRadius: '32px',
                padding: '3px',
              }}
            >
              <div className="bg-white p-8 sm:p-12" style={{ borderRadius: '30px' }}>
                <div className="grid md:grid-cols-2 gap-10 items-center">
                  {/* Photo */}
                  <div className="relative">
                    <div
                      className="absolute -top-4 -left-4 w-full h-full rounded-3xl"
                      style={{ backgroundColor: `${colors.coralLight}30` }}
                    ></div>
                    <div className="relative w-full aspect-square max-w-md mx-auto rounded-3xl overflow-hidden shadow-xl">
                      <img
                        src="/images/tsushima.jpg"
                        alt="対馬ルリ子"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>

                  {/* Bio */}
                  <div>
                    <div
                      className="inline-block px-4 py-1.5 text-xs font-bold tracking-widest uppercase text-white mb-4"
                      style={{ backgroundColor: colors.coral, borderRadius: '100px' }}
                    >
                      Keynote Speaker
                    </div>
                    <p className="text-base leading-7 text-slate-700">ホルモンを知ることは、人生を味方につけること。</p>
                    <h4
                      className="text-4xl font-black mb-3"
                      style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
                    >
                      対馬ルリ子
                    </h4>
                    <p className="text-base text-gray-600 mb-1">医師（対馬ルリ子女性ライフクリニック銀座　院長）</p>
                    <p className="text-base text-gray-500 mb-6">一般財団法人 日本女性財団 理事長</p>
                    <p className="text-gray-500 leading-relaxed mb-6 text-base">
                      妊娠・出産だけでは終わらない。<br />
                      思春期から更年期、老年期まで女性の一生を一続きで診る医療を、日本で切り拓いてきた医師。<br /><br />
                      女性総合外来の草分けとして、臨床・教育・政策をつなぎ、「女性の健康」を社会の言葉にしてきた。<br /><br />
                      医療は、治すためだけにあるのではない。<br />
                      自分の体を理解し、選び取る力を取り戻すためにある。<br /><br />
                      ホルモンは、振り回されるものではない。<br />
                      知れば、人生はもっと自由になる。
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {['女性ホルモン', '更年期医療', '予防医学'].map((tag, i) => (
                        <span
                          key={i}
                          className="px-4 py-2 text-base font-medium"
                          style={{
                            backgroundColor: colors.cream,
                            borderRadius: '100px',
                            color: colors.warmGray,
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Femship doctors */}
          <div className="mb-20">
            <div className="flex items-center gap-3 mb-10">
              <div
                className="w-12 h-12 flex items-center justify-center"
                style={{ backgroundColor: colors.mint, borderRadius: '12px' }}
              >
                <i className="ri-stethoscope-line text-xl text-white"></i>
              </div>
              <h3 className="text-3xl font-black" style={{ color: colors.warmGray }}>
                フェムシップドクター講演
              </h3>
            </div>

            <div className="grid sm:grid-cols-2 gap-8">
              {[
                {
                  comment: 'がんばりすぎないことは、立派な処方です。',
                  name: '髙宮城尚子',
                  credentials: '医師（Naoko女性クリニック　院長／沖縄）',
                  role: 'フェムシップドクター',
                  desc: '<p>沖縄で39年、<br>女性の体と“日常の不調”に向き合ってきた医師。</p><p>西洋医学（ホルモン剤）と東洋医学（漢方・鍼灸）を併用し、月経痛、PMS・更年期、冷え、名前のつかない違和感にも、丁寧に耳を傾ける。</p><p>髙宮城先生が繰り返し伝える言葉。「完璧主義をやめて心も身体もゆるめましょう。」</p><p>無理をしないことは、弱さではない。<br>それは、長く健やかに生きるための知恵。</p><p>医療は、強くするためだけでなく、力を抜くことを許すためにもある。</p>',
                  seq: 'takamiyagi-naoko-femship2',
                  src: '/images/takamiyagi.jpg',
                },
                {
                  comment: '三世代で、女性の一生を診てきた。',
                  name: '中島由美子',
                  credentials: '医師（下平レディースクリニック　院長／東京）',
                  role: 'フェムシップドクター',
                  desc: '<p>祖父の代から続く産婦人科医。<br>父の志と技術を受け継ぎ、<br>地域で女性の人生に寄り添い続けてきた医師。</p>' +
                  '<p>思春期の戸惑いも、<br>更年期の揺らぎも、<br>老年期の不安も——<br>「女性の体は、すべてつながっている」と知っている。時の流れとともに体は変化しても、1人1人に寄り添って共に乗り越えていく</p>' +
                  '<p>中島先生の診療の軸は、とてもシンプル。<br>まず、話を聴くこと。「一人で悩まないで。一緒に考えましょう」</p>' +
                  '<p>医療は、答えを押しつけるものではない。」<br>一緒に考え、伴走するためにある。</p>',
                  seq: 'nakajima-yumiko-femship2',
                  src: '/images/nakajima-2.jpg',
                },
              ].map((speaker, index) => (
                <div
                  key={index}
                  className="group bg-white p-8 transition-all duration-500 hover:-translate-y-2 hover:shadow-xl"
                  style={{ borderRadius: '24px', border: '1px solid #f0f0f0' }}
                >
                  <div
                    className="w-full aspect-square rounded-2xl overflow-hidden mb-6"
                    style={{ backgroundColor: colors.cream }}
                  >
                    <img
                      src={speaker.src} 
                      alt={speaker.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <p className="text-base text-gray-500 leading-relaxed mb-4">{speaker.comment}</p>
                  <h4 className="text-2xl font-bold mb-2" style={{ color: colors.warmGray }}>
                    {speaker.name}
                  </h4>
                  <p className="text-base text-gray-600 mb-4">
                    {speaker.credentials ?? speaker.role}
                  </p>
                  <div
                  className="text-base text-gray-500 leading-relaxed [&_p]:mt-3 [&_p:first-child]:mt-0"
                  dangerouslySetInnerHTML={{ __html: speaker.desc }}
                />
                </div>
              ))}
            </div>
          </div>

          {/* Talk session speakers */}
          <div>
            <div className="flex items-center gap-3 mb-10">
              <div
                className="w-12 h-12 flex items-center justify-center"
                style={{ backgroundColor: colors.coral, borderRadius: '12px' }}
              >
                <i className="ri-chat-3-line text-xl text-white"></i>
              </div>
              <div>
                <h3 className="text-3xl font-black" style={{ color: colors.warmGray }}>
                  トークセッション
                </h3>
                <p className="text-base text-gray-500 mt-1">「愛と希望で人生を切り拓く方法」</p>
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-8 mb-12">
              {[
                {
                  name: '富永喜代',
                  credentials: '医師（富永ペインクリニック　院長／愛媛）',
                  subtitle: '医学で、人生を最高化する。',
                  desc: 'ミリオンセラー作家、ReSex®人生パフォーマンス提唱者。麻酔実績2万例超の麻酔科医。YouTube登録30万人・総再生7,500万回。SNS総フォロワー46万人超。11坪7人の貧困家庭から、医師へ。医学は、治すためだけにあるのではない。人生を自分らしく生きるためにある。',
                  seq: 'tominaga-kiyo-talk-speaker',
                  src: '/images/tominaga.jpg',
                },
                {
                  name: '森田敦子',
                  credentials: '植物療法士（株式会社サンルイ・インターナショナル　代表取締役）',
                  subtitle: '植物の力で、女性の時間を整える。',
                  desc: '忙しさの中で体を壊した20代。喘息をきっかけに出会ったのが、植物療法。フランスで学び、日本にフィトテラピーを根づかせた第一人者。植物×科学で、肌・ホルモン・心・ライフステージを支え続けてきた。「もっと、自分を大切にしていい」そのメッセージを、暮らしに落とし込む実践者。',
                  seq: 'morita-atsuko-talk-speaker',
                  src: '/images/338784.jpg',
                },
                {
                  name: '中野裕弓',
                  credentials: 'ソーシャルファシリテーター／人事コンサルタント',
                  subtitle: '愛を循環させ、社会をあたためる。',
                  desc: '外資系銀行から、世界銀行本部へ。日本人初の人事マネージャーとして多様な人と組織の"間"に向き合ってきた。「世界がもしも100人の村だったら」原文訳者。そして、独自の"愛のコーヒーカップ理論"の提唱者。今は、社会をつなぐ輪＝Social Wreath構想のもと、一人ひとりの幸福度を高める対話を育てている。',
                  seq: 'nakano-hiroyumi-talk-speaker',
                  src: '/images/nakano.jpg',
                },
              ].map((speaker, index) => (
                <div
                  key={index}
                  className="group bg-white p-8 transition-all duration-500 hover:-translate-y-2 hover:shadow-xl"
                  style={{ borderRadius: '24px', border: '1px solid #f0f0f0' }}
                >
                  <div
                    className="w-full aspect-square rounded-2xl overflow-hidden mb-6"
                    style={{ backgroundColor: colors.cream }}
                  >
                    <img
                      src={speaker.src}
                      alt={speaker.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-leaf-line text-lg" style={{ color: colors.mint }}></i>
                      <span className="text-xs font-bold tracking-wider uppercase text-gray-400">登壇者</span>
                    </div>
                    <p className="text-lg font-medium mb-2" style={{ color: colors.coral }}>
                      {speaker.subtitle}
                    </p>
                  </div>

                  <h4 className="text-2xl font-bold mb-1" style={{ color: colors.warmGray }}>
                    {speaker.name}
                  </h4>
                  <p className="text-base text-gray-600 mb-4">
                    {speaker.credentials}
                  </p>
                  <div
                  className="text-base text-gray-500 leading-relaxed [&_p]:mt-3 [&_p:first-child]:mt-0"
                  dangerouslySetInnerHTML={{ __html: speaker.desc }}
                />
                </div>
              ))}
            </div>

            {/* Moderator */}
            <div className="bg-white p-8 shadow-lg" style={{ borderRadius: '24px' }}>
              <div className="grid md:grid-cols-3 gap-8 items-center">
                <div className="md:col-span-1">
                  <div
                    className="w-full aspect-square rounded-2xl overflow-hidden"
                    style={{ backgroundColor: colors.cream }}
                  >
                    <img
                      src="/images/tsushima.jpg"
                      alt="対馬ルリ子"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <div
                    className="inline-block px-3 py-1 text-xs font-bold tracking-widest uppercase text-white mb-3"
                    style={{ backgroundColor: colors.coral, borderRadius: '100px' }}
                  >
                    Moderator
                  </div>
                  <h4 className="text-3xl font-bold mb-2" style={{ color: colors.warmGray }}>
                    対馬ルリ子
                  </h4>
                  <p className="text-base text-gray-600 mb-1">医師（対馬ルリ子女性ライフクリニック銀座　院長）</p>
                  <p className="text-base text-gray-500 mb-4">一般財団法人 日本女性財団 理事長</p>
                  <p className="text-base text-gray-500 leading-relaxed">
                    女性ホルモンと健康に関する第一人者として、トークセッションのモデレーターを務めます。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* -------------------------------------------------
          Experience contents
      ------------------------------------------------- */}
      <section
        id="experience"
        data-animate
        className="py-28 bg-white relative overflow-hidden"
      >
        <div
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${
            isVisible['experience'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-20">
            <span
              className="inline-block px-4 py-1.5 text-xs font-bold tracking-widest uppercase mb-6"
              style={{
                backgroundColor: `${colors.mintLight}60`,
                color: colors.mint,
                borderRadius: '100px',
              }}
            >
              Experience
            </span>
            <h2
              className="text-5xl sm:text-6xl font-black mb-6"
              style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
            >
              心が動く体験プログラム
            </h2>
            <p className="text-xl text-gray-500">キモノの美、想いをつなぐチャリティー、そして新しい出会い</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-vip-diamond-line',
                title: 'キモノビューティーショー',
                desc: '日本の美をまとう瞬間、女性はもっと自由になる。世界を魅了したキモノビューティが、愛と希望の物語をランウエイで描きます。',
                color: colors.coral,
                image:
                  '/images/S__42582141_0.jpg',
              },
              {
                icon: 'ri-auction-line',
                title: 'チャリティ・アートオークション',
                desc: '特別なアイテムやアートが並ぶチャリティオークション。楽しみながら参加でき、その一歩が女性支援活動へと繋がります。',
                color: colors.mint,
                image:
                  '/images/DSC_0016.jpg',
              },
              {
                icon: 'ri-group-line',
                title: '交流タイム',
                desc: '同じ想いを持つ参加者同士が出会い、つながる貴重な時間。新たな気づきや出会いが生まれます。',
                color: colors.coral,
                image:
                  '/images/koryu.jpg',
              },
            ].map((item, index) => (
              <div
                key={index}
                className="group relative overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl flex flex-col"
                style={{ borderRadius: '24px' }}
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden flex-shrink-0">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                </div>

                {/* Content overlay */}
                <div
                  className="p-8 text-white flex-grow flex flex-col"
                  style={{
                    background: `linear-gradient(135deg, ${item.color} 0%, ${item.color}dd 100%)`,
                  }}
                >
                  <div
                    className="w-12 h-12 flex items-center justify-center mb-4"
                    style={{ backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '12px' }}
                  >
                    <i className={`${item.icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-2xl font-bold mb-3">{item.title}</h3>
                  <p className="text-white/80 leading-relaxed text-base">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* -------------------------------------------------
          Target audience
      ------------------------------------------------- */}
      <section
        id="target"
        data-animate
        className="py-28 relative overflow-hidden"
        style={{ backgroundColor: colors.cream }}
      >
        <div
          className={`max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${
            isVisible['target'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Text side */}
            <div>
              <span
                className="inline-block px-4 py-1.5 text-xs font-bold tracking-widest uppercase mb-6"
                style={{
                  backgroundColor: `${colors.coralLight}40`,
                  color: colors.coral,
                  borderRadius: '100px',
                }}
              >
                For You
              </span>
              <h2
                className="text-5xl sm:text-6xl font-black mb-8 leading-tight"
                style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
              >
                こんな方に<br />
                おすすめ
              </h2>
              <p className="text-gray-500 mb-8 leading-relaxed text-lg">
                女性の健康やWell-beingに関心がある全ての方のご参加をお待ちしています。
              </p>
              <a
                href="https://peatix.com/event/4805808/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 text-lg font-bold text-white whitespace-nowrap cursor-pointer rounded-xl bg-[#7ECEC1] hover:bg-[#6ebdb0] transition-colors"
              >
                <span>参加を申し込む</span>
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            {/* Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: 'ri-heart-line', text: '女性の健康やWell-beingに関心がある方' },
                { icon: 'ri-calendar-check-line', text: 'ライフステージの変化に向き合っている方' },
                { icon: 'ri-stethoscope-line', text: '医療・健康分野の専門家や関係者' },
                { icon: 'ri-hand-heart-line', text: '社会貢献活動に参加したい方' },
                { icon: 'ri-palette-line', text: '文化・芸術を楽しみたい方' },
                { icon: 'ri-user-add-line', text: '新しい出会いやつながりを求めている方' },
              ].map((item, index) => (
                <div
                  key={index}
                  className="group p-5 bg-white transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                  style={{ borderRadius: '16px' }}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="w-10 h-10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                      style={{
                        backgroundColor: index % 2 === 0 ? `${colors.coralLight}40` : `${colors.mintLight}60`,
                        borderRadius: '10px',
                      }}
                    >
                      <i
                        className={`${item.icon} text-lg`}
                        style={{ color: index % 2 === 0 ? colors.coral : colors.mint }}
                      ></i>
                    </div>
                    <p className="text-base font-medium text-gray-600 leading-snug">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* -------------------------------------------------
          Donation usage
      ------------------------------------------------- */}
      <section
        id="donation"
        data-animate
        className="py-28 bg-white relative overflow-hidden"
      >
        <div
          className={`max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${
            isVisible['donation'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-16">
            <span
              className="inline-block px-4 py-1.5 text-xs font-bold tracking-widest uppercase mb-6"
              style={{
                backgroundColor: `${colors.mintLight}60`,
                color: colors.mint,
                borderRadius: '100px',
              }}
            >
              Donation
            </span>
            <h2
              className="text-5xl sm:text-6xl font-black mb-6"
              style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
            >
              寄付金の使途
            </h2>
            <p className="text-xl text-gray-500">参加費の一部は、以下の活動に充てられます</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {[
              {
                icon: 'ri-heart-pulse-line',
                title: '女性の健康支援・相談事業',
                desc: '専門医による健康相談窓口の運営や、オンライン相談サービスの提供',
              },
              {
                icon: 'ri-book-2-line',
                title: 'ライフステージ別の啓発・教育',
                desc: '思春期、妊娠・出産期、更年期など、各ステージに応じた健康教育プログラム',
              },
              {
                icon: 'ri-search-line',
                title: '調査・研究、政策提言',
                desc: '女性の健康課題に関する実態調査や、エビデンスに基づいた政策提言活動',
              },
              {
                icon: 'ri-team-line',
                title: '持続可能な支援体制構築',
                desc: '医療機関、企業、自治体との連携強化や、全国的な支援ネットワークの構築',
              },
            ].map((item, index) => (
              <div
                key={index}
                className="group p-8 bg-white transition-all duration-500 hover:-translate-y-1 hover:shadow-xl"
                style={{ borderRadius: '20px', border: '1px solid #f0f0f0' }}
              >
                <div className="flex gap-5">
                  <div
                    className="w-14 h-14 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                    style={{ backgroundColor: index % 2 === 0 ? colors.coral : colors.mint, borderRadius: '14px' }}
                  >
                    <i className={`${item.icon} text-2xl text-white`}></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-xl mb-2" style={{ color: colors.warmGray }}>
                      {item.title}
                    </h3>
                    <p className="text-gray-500 leading-relaxed text-base">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* -------------------------------------------------
          Ticket & receipt section
      ------------------------------------------------- */}
      <section
        id="ticket-section"
        data-animate
        className="py-28 relative overflow-hidden"
        style={{ backgroundColor: colors.cream }}
      >
        <div
          className={`max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${
            isVisible['ticket-section'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-16">
            <span
              className="inline-block px-4 py-1.5 text-xs font-bold tracking-widest uppercase mb-6"
              style={{
                backgroundColor: `${colors.coralLight}40`,
                color: colors.coral,
                borderRadius: '100px',
              }}
            >
              Ticket Info
            </span>
            <h2
              className="text-5xl sm:text-6xl font-black mb-6"
              style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
            >
              チケット・領収書
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Ticket purchase */}
            <div className="bg-white p-8 shadow-lg" style={{ borderRadius: '24px' }}>
              <div className="w-14 h-14 flex items-center justify-center mb-6" style={{ backgroundColor: colors.coral, borderRadius: '14px' }}>
                <i className="ri-ticket-2-line text-2xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold mb-4" style={{ color: colors.warmGray }}>
                チケット購入
              </h3>
              <p className="text-gray-500 leading-relaxed mb-6 text-base">
                チケットはPeatixにてオンライン購入いただけます。クレジットカード、コンビニ決済、ATM決済に対応。
              </p>
              <div className="p-6" style={{ backgroundColor: colors.cream, borderRadius: '16px' }}>
                <div className="text-base text-gray-500 mb-1">参加費</div>
                <div className="text-5xl font-black" style={{ color: colors.coral }}>
                  ¥10,000<span className="text-lg font-medium text-gray-500 ml-2">（税込）</span>
                </div>
              </div>
            </div>

            {/* Receipt info */}
            <div className="bg-white p-8 shadow-lg" style={{ borderRadius: '24px' }}>
              <div className="w-14 h-14 flex items-center justify-center mb-6" style={{ backgroundColor: colors.mint, borderRadius: '14px' }}>
                <i className="ri-file-text-line text-2xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold mb-4" style={{ color: colors.warmGray }}>
                領収書の発行
              </h3>
              <p className="text-gray-500 leading-relaxed mb-6 text-base">
                領収書が必要な方は、Peatixの購入履歴ページより発行いただけます。
              </p>

              <button
                onClick={() => {
                  const content = document.getElementById('receipt-detail');
                  if (content) {
                    content.classList.toggle('hidden');
                  }
                }}
                className="flex items-center gap-2 text-base font-bold cursor-pointer hover:opacity-80 transition-opacity"
                style={{ color: colors.mint }}
              >
                <span>法人一括領収書について</span>
                <i className="ri-arrow-down-s-line text-lg"></i>
              </button>

              <div id="receipt-detail" className="hidden mt-4 p-4 rounded-xl" style={{ backgroundColor: colors.cream }}>
                <p className="text-base text-gray-500 leading-relaxed mb-3">
                  複数名分をまとめた法人一括領収書をご希望の場合は、以下の手順でお申し込みください。
                </p>
                <ol className="text-base text-gray-500 space-y-2 list-decimal list-inside">
                  <li>Peatixで必要枚数分のチケットを購入</li>
                  <li>購入完了後、下記メールアドレスまでご連絡</li>
                  <li>件名に「法人一括領収書希望」と明記</li>
                </ol>
                <p className="text-base text-gray-500 mt-3">
                  <strong>連絡先：</strong>info@iwd-japanwomen.org
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* -------------------------------------------------
          Venue
      ------------------------------------------------- */}
      <section
        id="venue"
        data-animate
        className="py-28 bg-white relative overflow-hidden"
      >
        <div
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${
            isVisible['venue'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-16">
            <span
              className="inline-block px-4 py-1.5 text-xs font-bold tracking-widest uppercase mb-6"
              style={{
                backgroundColor: `${colors.mintLight}60`,
                color: colors.mint,
                borderRadius: '100px',
              }}
            >
              Venue
            </span>
            <h2
              className="text-5xl sm:text-6xl font-black mb-6"
              style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
            >
              会場・アクセス
            </h2>
          </div>

          {/* 建物写真ギャラリー - ダイナミックなレイアウト */}
          <div className="mb-16 relative">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 min-h-[320px] md:min-h-[400px]">
              {/* メイン写真 - TODA BUILDING 外観 */}
              <div className="md:col-span-7 relative group">
                <div className="absolute -bottom-3 -right-3 w-full h-full rounded-3xl" style={{ backgroundColor: `${colors.mintLight}60` }}></div>
                <div className="relative h-64 md:h-full min-h-[240px] rounded-3xl overflow-hidden shadow-xl transform md:-rotate-1 group-hover:rotate-0 transition-transform duration-500">
                  <img
                    src="/images/S__42582139_0.jpg"
                    alt="TODA BUILDING 外観"
                    className="w-full h-full object-cover object-center"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>
              </div>
              {/* 右側: 2枚縦積み */}
              <div className="md:col-span-5 flex flex-col gap-4 md:gap-6">
                <div className="flex-1 relative group">
                  <div className="absolute -top-2 -left-2 w-full h-full rounded-2xl" style={{ backgroundColor: `${colors.coralLight}50` }}></div>
                  <div className="relative h-48 md:h-full min-h-[180px] rounded-2xl overflow-hidden shadow-lg transform md:rotate-1 group-hover:rotate-0 transition-transform duration-500">
                    <img
                      src="/images/S__42582138_0.jpg"
                      alt="TODA BUILDING エントランス"
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                </div>
                <div className="flex-1 relative group">
                  <div className="absolute -bottom-2 -right-2 w-full h-full rounded-2xl" style={{ backgroundColor: `${colors.mintLight}70` }}></div>
                  <div className="relative h-48 md:h-full min-h-[180px] rounded-2xl overflow-hidden shadow-lg transform md:-rotate-2 group-hover:rotate-0 transition-transform duration-500">
                    <img
                      src="/images/S__42582136_0.jpg"
                      alt="TODA BUILDING 内装"
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left side – info */}
            <div className="p-8 sm:p-10" style={{ backgroundColor: colors.cream, borderRadius: '24px' }}>
              <h3 className="text-4xl font-black mb-8" style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}>
                TODAホール
              </h3>

              <div className="space-y-4 mb-8">
                {/* Address */}
                <div className="flex gap-4 p-4 bg-white shadow-sm" style={{ borderRadius: '16px' }}>
                  <div className="w-12 h-12 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: colors.coral, borderRadius: '12px' }}>
                    <i className="ri-map-pin-line text-xl text-white"></i>
                  </div>
                  <div>
                    <div className="font-bold mb-1 text-lg" style={{ color: colors.warmGray }}>
                      住所
                    </div>
                    <div className="text-gray-500 text-base">
                      〒104-0031<br />
                      東京都中央区京橋1-7-1<br />
                      TODA BUILDING 4階
                    </div>
                  </div>
                </div>

                {/* Access */}
                <div className="flex gap-4 p-4 bg-white shadow-sm" style={{ borderRadius: '16px' }}>
                  <div className="w-12 h-12 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: colors.mint, borderRadius: '12px' }}>
                    <i className="ri-train-line text-xl text-white"></i>
                  </div>
                  <div>
                    <div className="font-bold mb-1 text-lg" style={{ color: colors.warmGray }}>
                      アクセス
                    </div>
                    <div className="text-gray-500 space-y-1 text-base">
                      <p>・東京メトロ銀座線「京橋駅」6番出口より徒歩2分</p>
                      <p>・都営浅草線「宝町駅」A4出口より徒歩3分</p>
                      <p>・JR「東京駅」八重洲中央口より徒歩8分</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-white" style={{ borderRadius: '12px', border: `1px solid ${colors.coralLight}` }}>
                <div className="flex items-center gap-2 text-base text-gray-600">
                  <i className="ri-information-line text-xl" style={{ color: colors.coral }}></i>
                  <span>駐車場のご用意はございません。公共交通機関をご利用ください。</span>
                </div>
              </div>
            </div>

            {/* Right side – map */}
            <div className="relative">
              <div
                className="absolute -top-4 -right-4 w-full h-full"
                style={{ backgroundColor: `${colors.mintLight}50`, borderRadius: '24px' }}
              ></div>
              <div className="relative w-full h-96 rounded-3xl overflow-hidden shadow-xl">
                <iframe
                  src="https://maps.google.com/maps?q=TODA+HALL+%26+CONFERENCE+TOKYO+%E4%BA%AC%E6%A9%8B&z=17&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="TODAホール 地図"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* -------------------------------------------------
          FAQ
      ------------------------------------------------- */}
      <section
        id="faq"
        data-animate
        className="py-28 bg-white relative overflow-hidden"
      >
        <div
          className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${
            isVisible['faq'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-16">
            <span
              className="inline-block px-4 py-1.5 text-xs font-bold tracking-widest uppercase mb-6"
              style={{
                backgroundColor: `${colors.mintLight}60`,
                color: colors.mint,
                borderRadius: '100px',
              }}
            >
              FAQ
            </span>
            <h2
              className="text-5xl sm:text-6xl font-black mb-6"
              style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
            >
              よくあるご質問
            </h2>
          </div>

          <div className="space-y-4">
            {[
              {
                q: '服装に決まりはありますか？',
                a: 'ドレスコードは「愛と希望の翼」です。自由なスタイルでご参加いただけます。カジュアルからフォーマルまで、ご自身が心地よいと感じる服装でお越しください。',
              },
              {
                q: '途中参加・途中退出は可能ですか？',
                a: 'はい、可能です。ただし、講演中の入退室は他の参加者のご迷惑となる場合がございますので、休憩時間をご利用いただくことをおすすめします。',
              },
              {
                q: '男性も参加できますか？',
                a: 'もちろんです。女性の健康課題は社会全体で取り組むべきテーマです。性別を問わず、関心をお持ちの方のご参加を歓迎いたします。',
              },
              {
                q: 'キャンセルポリシーについて教えてください',
                a: 'Peatixの規約に準じます。イベント開催日の7日前までは全額返金、それ以降のキャンセルは返金対象外となります。',
              },
              {
                q: '領収書は発行できますか？',
                a: 'はい、Peatixの購入履歴ページより発行いただけます。法人一括領収書をご希望の場合は、info@iwd-japanwomen.org までお問い合わせください。',
              },
              {
                q: '支払い方法は何がありますか？',
                a: 'Peatixにて、クレジットカード、コンビニ決済、ATM決済がご利用いただけます。',
              },
            ].map((faq, index) => (
              <div
                key={index}
                className="bg-white overflow-hidden shadow-sm transition-shadow hover:shadow-lg"
                style={{ borderRadius: '16px' }}
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-8 py-6 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <span className="font-bold text-left pr-4 text-lg" style={{ color: colors.warmGray }}>
                    {faq.q}
                  </span>
                  <div
                    className={`w-10 h-10 flex items-center justify-center flex-shrink-0 transition-all ${openFaq === index ? 'rotate-180' : ''}`}
                    style={{ backgroundColor: colors.cream, borderRadius: '10px' }}
                  >
                    <i className="ri-arrow-down-s-line text-xl" style={{ color: colors.coral }}></i>
                  </div>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96' : 'max-h-0'}`}>
                  <div className="px-8 pb-6">
                    <p className="text-gray-500 leading-relaxed text-base">{faq.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== 協賛ブロック ========== */}
      <div className="relative bg-white">
      {/* -------------------------------------------------
          Sponsorship
      ------------------------------------------------- */}
      <section
        id="sponsorship"
        data-animate
        className="py-28 relative overflow-hidden z-10 scroll-mt-24"
      >
        <div
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${
            isVisible['sponsorship'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-20">
            <span
              className="inline-block px-4 py-1.5 text-xs font-bold tracking-widest uppercase mb-6"
              style={{
                backgroundColor: `${colors.mintLight}60`,
                color: colors.mint,
                borderRadius: '100px',
              }}
            >
              Sponsorship
            </span>
            <h2
              className="text-5xl sm:text-6xl font-black mb-6"
              style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
            >
              協賛プランについて
            </h2>
            <p className="text-xl text-gray-500 max-w-3xl mx-auto">
              女性の健康と活躍を支援する本イベントへの協賛を通じて，
              <br className="hidden sm:block" />
              貴社のDE&I推進への姿勢を社内外に発信しませんか
            </p>
          </div>

          {/* Recommended companies */}
          <div className="p-8 sm:p-12 mb-16" style={{ backgroundColor: colors.cream, borderRadius: '24px' }}>
            <h3 className="text-3xl font-black mb-8 flex items-center gap-3" style={{ color: colors.warmGray }}>
              <div className="w-12 h-12 flex items-center justify-center" style={{ backgroundColor: colors.coral, borderRadius: '12px' }}>
                <i className="ri-building-line text-2xl text-white"></i>
              </div>
              本協賛を特におすすめしたい企業様
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                '女性社員比率が高い、または向上を目指している企業様',
                '人的資本経営・健康経営・DE&Iを重要な経営テーマとしている企業様',
                '社会との共創を企業価値向上の一環として推進されている企業様',
              ].map((text, index) => (
                <div
                  key={index}
                  className="flex gap-3 p-5 bg-white shadow-sm"
                  style={{ borderRadius: '16px' }}
                >
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${colors.coralLight}40`, borderRadius: '8px' }}>
                    <i className="ri-checkbox-circle-fill text-lg" style={{ color: colors.coral }}></i>
                  </div>
                  <p className="text-base text-gray-700 font-medium">{text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Benefits */}
          <div className="mb-16">
            <h3 className="text-3xl font-black mb-10 text-center" style={{ color: colors.warmGray }}>
              協賛のメリット
            </h3>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { icon: 'ri-megaphone-line', title: 'PR・広報活動', desc: '自社ホームページ、社内報、統合報告書、サステナビリティレポート等での実績活用', color: colors.coral },
                { icon: 'ri-user-heart-line', title: 'エンゲージメント向上', desc: '社員エンゲージメントの向上や採用広報の強化に貢献', color: colors.mint },
                { icon: 'ri-award-line', title: 'ブランド価値向上', desc: 'DE&I推進に積極的に取り組む企業としての姿勢を「見える形」で発信', color: colors.coral },
              ].map((item, index) => (
                <div key={index} className="group text-center p-8 transition-all duration-300 hover:-translate-y-2" style={{ backgroundColor: colors.cream, borderRadius: '20px' }}>
                  <div
                    className="w-16 h-16 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform"
                    style={{ backgroundColor: item.color, borderRadius: '16px' }}
                  >
                    <i className={`${item.icon} text-3xl text-white`}></i>
                  </div>
                  <h4 className="text-2xl font-bold mb-3" style={{ color: colors.warmGray }}>
                    {item.title}
                  </h4>
                  <p className="text-base text-gray-500">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Sponsorship plans - serif統一・極端なジャンプ率・レベル別デザイン */}
          <h3 className="text-3xl font-black mb-10 text-center" style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}>
            協賛プラン一覧
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {[
              {
                name: 'ダイヤモンド',
                price: '30万円〜',
                theme: 'diamond' as const,
                features: ['ステージPR（5分）', 'ホワイエブース（L）', 'ロゴ掲載（大）'],
                badge: '最上位プラン',
                layer: 'bg-[#2d2d44]/50',
                layerRotate: '-2deg',
                contentClass: 'bg-[#0f1419] text-white',
                nameSize: 'text-2xl',
                priceSize: 'text-3xl',
                accentLine: 'bg-[#d4af37]',
                featureColor: 'text-white/85',
                checkColor: '#d4af37',
                badgeClass: 'text-[#d4af37]/90',
                orClass: 'text-white/50',
                priceColor: '#d4af37',
              },
              {
                name: 'プラチナ',
                price: '20万円',
                theme: 'platinum' as const,
                features: ['ステージPR（3分）', 'または', 'ホワイエブース（M）'],
                badge: '人気プラン',
                layer: 'bg-slate-200/60',
                layerRotate: '1.5deg',
                contentClass: 'bg-slate-50',
                nameSize: 'text-xl',
                priceSize: 'text-3xl',
                accentLine: 'bg-slate-400',
                featureColor: 'text-slate-600',
                checkColor: '#64748b',
                badgeClass: 'text-slate-600',
                orClass: 'text-slate-400',
                priceColor: '#475569',
              },
              {
                name: 'ゴールド',
                price: '10万円',
                theme: 'gold' as const,
                features: ['ステージPR（1分）', 'または', 'ホワイエブース（S）'],
                badge: null,
                layer: 'bg-amber-200/50',
                layerRotate: '-1deg',
                contentClass: 'bg-amber-50/90',
                nameSize: 'text-lg',
                priceSize: 'text-2xl',
                accentLine: 'bg-amber-500',
                featureColor: 'text-amber-900/80',
                checkColor: '#d97706',
                badgeClass: '',
                orClass: 'text-amber-600/70',
                priceColor: '#b45309',
              },
              {
                name: 'ルビー',
                price: '物品協賛',
                theme: 'ruby' as const,
                features: ['ロゴ掲載', '抽選会景品として紹介'],
                badge: null,
                layer: 'bg-[#f5c6c6]/60',
                layerRotate: '2deg',
                contentClass: 'bg-white',
                nameSize: 'text-base',
                priceSize: 'text-xl',
                accentLine: 'bg-[#E8847C]',
                featureColor: 'text-gray-600',
                checkColor: '#E8847C',
                badgeClass: '',
                orClass: 'text-gray-400',
                priceColor: '#c45c5c',
              },
            ].map((plan, index) => (
              <div
                key={index}
                className="group relative transition-transform duration-300 hover:-translate-y-1"
              >
                {/* 背面レイヤー（角度＋ブラー、カード本体は真っ直ぐ） */}
                <div
                  className={`absolute -top-3 -left-3 w-[calc(100%+24px)] h-[calc(100%+24px)] rounded-2xl blur-md ${plan.layer}`}
                  style={{ transform: `rotate(${plan.layerRotate})` }}
                />
                {/* カード本体 */}
                <div
                  className={`relative rounded-2xl overflow-hidden h-full ${plan.contentClass}`}
                  style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}
                >
                  <div className="p-6 sm:p-7 flex flex-col min-h-[280px]">
                      {plan.badge && (
                        <span
                          className={`inline-block px-3 py-1 text-[10px] font-bold tracking-[0.15em] uppercase mb-4 self-start ${plan.badgeClass}`}
                          style={{ fontFamily: "'Shippori Mincho', serif", borderRadius: '4px' }}
                        >
                          {plan.badge}
                        </span>
                      )}
                      {!plan.badge && <div className="h-8 mb-4" />}

                      {/* プラン名 */}
                      <h4
                        className={`${plan.nameSize} font-bold mb-2 tracking-tight`}
                        style={{ fontFamily: "'Shippori Mincho', serif", color: index === 0 ? 'white' : colors.warmGray }}
                      >
                        {plan.name}
                      </h4>

                      {/* 価格 - ジャンプ率強調 */}
                      <p
                        className={`${plan.priceSize} font-black mb-5 tracking-tight`}
                        style={{
                          fontFamily: "'Shippori Mincho', serif",
                          color: plan.priceColor,
                        }}
                      >
                        {plan.price}
                      </p>

                      {/* 区切り */}
                      <div className={`w-10 sm:w-12 h-px mb-4 ${plan.accentLine} opacity-60`} />

                      {/* 特典 */}
                      <div className="space-y-2 flex-grow">
                        {plan.features.map((feature, i) => (
                          <div key={i} className="flex items-start gap-3">
                            {feature !== 'または' ? (
                              <>
                                <span
                                  className="inline-block w-4 h-4 flex-shrink-0 mt-0.5 rounded-full border-2"
                                  style={{ borderColor: plan.checkColor }}
                                />
                                <span className={`text-xs sm:text-sm font-medium leading-relaxed ${plan.featureColor}`} style={{ fontFamily: "'Shippori Mincho', serif" }}>
                                  {feature}
                                </span>
                              </>
                            ) : (
                              <span className={`text-xs ml-6 font-normal ${plan.orClass}`} style={{ fontFamily: "'Shippori Mincho', serif" }}>{feature}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                </div>
              </div>
            ))}
          </div>

          {/* Call‑to‑action */}
          <div className="text-center">
            <p className="text-gray-500 mb-8 text-lg">
              協賛に関するお問い合わせ・お申し込みは下記よりご連絡ください
            </p>
            <div className="flex flex-wrap items-center justify-center gap-6">
              <button
                onClick={() => setShowSponsorForm(true)}
                className="inline-flex items-center gap-3 px-6 py-3 text-xl font-bold text-white whitespace-nowrap cursor-pointer rounded-xl bg-[#7ECEC1] hover:bg-[#6ebdb0] transition-colors"
              >
                <i className="ri-mail-send-line text-2xl"></i>
                協賛のお問い合わせ
              </button>
              <Link
                to="/sponsor-guide"
                className="inline-flex items-center gap-3 px-6 py-3 text-lg font-bold whitespace-nowrap rounded-xl transition-colors hover:opacity-90"
                style={{
                  backgroundColor: '#f0fdf8',
                  color: colors.warmGray,
                  border: `1px solid ${colors.mintLight}`,
                }}
              >
                <i className="ri-file-list-3-line text-xl" style={{ color: colors.mint }}></i>
                協賛企業様向けの詳細案内はこちら
                <i className="ri-arrow-right-s-line text-lg" style={{ color: colors.mint }}></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* -------------------------------------------------
          Sponsors list
      ------------------------------------------------- */}
      <section
        id="sponsors"
        data-animate
        className="py-28 relative overflow-hidden z-10"
      >
        <div
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${
            isVisible['sponsors'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* 企業一覧エリア：薄いグレー背景で統一 */}
          <div className="rounded-2xl p-8 sm:p-10 pb-10" style={{ backgroundColor: '#f5f5f5' }}>
          <div className="text-center mb-20">
            <span
              className="inline-block px-4 py-1.5 text-xs font-bold tracking-widest uppercase mb-6"
              style={{
                backgroundColor: `${colors.mintLight}60`,
                color: colors.mint,
                borderRadius: '100px',
              }}
            >
              Sponsors
            </span>
            <h2
              className="text-5xl sm:text-6xl font-black mb-6"
              style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
            >
              協賛企業一覧
            </h2>
            <p className="text-xl text-gray-500">本イベントをご支援いただいている企業様</p>
            <p className="text-xl text-gray-500">バナーから各企業様のサイトへリンクしています。（一部企業様はサイトが未公開の場合があります）</p>
          </div>

          {/* Diamond sponsors */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ backgroundColor: colors.coral }}>
                <i className="ri-vip-diamond-line text-xl text-white"></i>
              </div>
              <h3 className="text-2xl font-black" style={{ color: colors.warmGray }}>
                ダイヤモンドスポンサー
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 items-stretch">
              {sortByDisplayOrder(sponsors.filter((s) => s.plan === 'diamond')).map((s) => (
                <div key={s.id} className="flex flex-col items-center p-2">
                  {s.logo_url ? (
                    <>
                      {s.url ? (
                        <a href={s.url} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center w-full">
                          <div
                            className="w-full flex items-center justify-center p-4 flex-shrink-0"
                            style={{
                              height: '160px',
                              backgroundColor: '#ffffff',
                              borderRadius: '16px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                            }}
                          >
                            <img src={s.logo_url} alt={s.name} className="max-w-full max-h-[128px] w-auto h-auto object-contain" />
                          </div>
                          <span className="mt-2 text-xs text-gray-600 flex-shrink-0">{s.name}</span>
                        </a>
                      ) : (
                        <div className="flex flex-col items-center w-full">
                          <div
                            className="w-full flex items-center justify-center p-4 flex-shrink-0"
                            style={{
                              height: '160px',
                              backgroundColor: '#ffffff',
                              borderRadius: '16px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                            }}
                          >
                            <img src={s.logo_url} alt={s.name} className="max-w-full max-h-[128px] w-auto h-auto object-contain" />
                          </div>
                          <span className="mt-2 text-xs text-gray-600 flex-shrink-0">{s.name}</span>
                        </div>
                      )}
                    </>
                  ) : s.url ? (
                    <a href={s.url} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center w-full">
                      <div
                        className="w-full flex items-center justify-center p-4 flex-shrink-0"
                        style={{
                          height: '160px',
                          backgroundColor: '#ffffff',
                          borderRadius: '16px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        }}
                      >
                        <span className="text-lg font-bold text-center" style={{ color: colors.warmGray }}>{s.name}</span>
                      </div>
                    </a>
                  ) : (
                    <div
                      className="w-full flex items-center justify-center p-4 flex-shrink-0"
                      style={{
                        height: '160px',
                        backgroundColor: '#ffffff',
                        borderRadius: '16px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      }}
                    >
                      <span className="text-lg font-bold text-center" style={{ color: colors.warmGray }}>{s.name}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Platinum sponsors */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ backgroundColor: colors.mint }}>
                <i className="ri-medal-line text-xl text-white"></i>
              </div>
              <h3 className="text-2xl font-black" style={{ color: colors.warmGray }}>
                プラチナスポンサー
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 items-stretch">
              {sortByDisplayOrder(sponsors.filter((s) => s.plan === 'platinum')).map((s) => (
                <div key={s.id} className="flex flex-col items-center p-2">
                  {s.logo_url ? (
                    <>
                      {s.url ? (
                        <a href={s.url} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center w-full">
                          <div
                            className="w-full flex items-center justify-center p-4 flex-shrink-0"
                            style={{
                              height: '120px',
                              backgroundColor: '#ffffff',
                              borderRadius: '12px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                            }}
                          >
                            <img src={s.logo_url} alt={s.name} className="max-w-full max-h-[88px] w-auto h-auto object-contain" />
                          </div>
                          <span className="mt-2 text-xs text-gray-600 flex-shrink-0">{s.name}</span>
                        </a>
                      ) : (
                        <div className="flex flex-col items-center w-full">
                          <div
                            className="w-full flex items-center justify-center p-4 flex-shrink-0"
                            style={{
                              height: '120px',
                              backgroundColor: '#ffffff',
                              borderRadius: '12px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                            }}
                          >
                            <img src={s.logo_url} alt={s.name} className="max-w-full max-h-[88px] w-auto h-auto object-contain" />
                          </div>
                          <span className="mt-2 text-xs text-gray-600 flex-shrink-0">{s.name}</span>
                        </div>
                      )}
                    </>
                  ) : s.url ? (
                    <a href={s.url} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center w-full">
                      <div
                        className="w-full flex items-center justify-center p-4 flex-shrink-0"
                        style={{
                          height: '120px',
                          backgroundColor: '#ffffff',
                          borderRadius: '12px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        }}
                      >
                        <span className="text-base font-bold text-center" style={{ color: colors.warmGray }}>{s.name}</span>
                      </div>
                    </a>
                  ) : (
                    <div
                      className="w-full flex items-center justify-center p-4 flex-shrink-0"
                      style={{
                        height: '120px',
                        backgroundColor: '#ffffff',
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      }}
                    >
                      <span className="text-base font-bold text-center" style={{ color: colors.warmGray }}>{s.name}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Gold sponsors */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ backgroundColor: colors.coral }}>
                <i className="ri-copper-coin-line text-xl text-white"></i>
              </div>
              <h3 className="text-2xl font-black" style={{ color: colors.warmGray }}>
                ゴールドスポンサー
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 items-stretch">
              {sortByDisplayOrder(sponsors.filter((s) => s.plan === 'gold')).map((s) => (
                <div key={s.id} className="flex flex-col items-center p-2">
                  {s.logo_url ? (
                    <>
                      {s.url ? (
                        <a href={s.url} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center w-full">
                          <div
                            className="w-full flex items-center justify-center p-4 flex-shrink-0"
                            style={{
                              height: '100px',
                              backgroundColor: '#ffffff',
                              borderRadius: '10px',
                              boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                            }}
                          >
                            <img src={s.logo_url} alt={s.name} className="max-w-full max-h-[72px] w-auto h-auto object-contain" />
                          </div>
                          <span className="mt-1.5 text-[11px] text-gray-600 flex-shrink-0">{s.name}</span>
                        </a>
                      ) : (
                        <div className="flex flex-col items-center w-full">
                          <div
                            className="w-full flex items-center justify-center p-4 flex-shrink-0"
                            style={{
                              height: '100px',
                              backgroundColor: '#ffffff',
                              borderRadius: '10px',
                              boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                            }}
                          >
                            <img src={s.logo_url} alt={s.name} className="max-w-full max-h-[72px] w-auto h-auto object-contain" />
                          </div>
                          <span className="mt-1.5 text-[11px] text-gray-600 flex-shrink-0">{s.name}</span>
                        </div>
                      )}
                    </>
                  ) : s.url ? (
                    <a href={s.url} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center w-full">
                      <div
                        className="w-full flex items-center justify-center p-4 flex-shrink-0"
                        style={{
                          height: '100px',
                          backgroundColor: '#ffffff',
                          borderRadius: '10px',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                        }}
                      >
                        <span className="text-sm font-bold text-center" style={{ color: colors.warmGray }}>{s.name}</span>
                      </div>
                    </a>
                  ) : (
                    <div
                      className="w-full flex items-center justify-center p-4 flex-shrink-0"
                      style={{
                        height: '100px',
                        backgroundColor: '#ffffff',
                        borderRadius: '10px',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                      }}
                    >
                      <span className="text-sm font-bold text-center" style={{ color: colors.warmGray }}>{s.name}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Ruby sponsors */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ backgroundColor: colors.mint }}>
                <i className="ri-heart-3-line text-xl text-white"></i>
              </div>
              <h3 className="text-2xl font-black" style={{ color: colors.warmGray }}>
                ルビースポンサー
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-3 items-stretch">
              {sortByDisplayOrder(sponsors.filter((s) => s.plan === 'ruby')).map((s) => (
                <div key={s.id} className="flex flex-col items-center p-2">
                  {s.logo_url ? (
                    <>
                      {s.url ? (
                        <a href={s.url} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center w-full">
                          <div
                            className="w-full flex items-center justify-center p-4 flex-shrink-0"
                            style={{
                              height: '80px',
                              backgroundColor: '#ffffff',
                              borderRadius: '8px',
                              boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                            }}
                          >
                            <img src={s.logo_url} alt={s.name} className="max-w-full max-h-[48px] w-auto h-auto object-contain" />
                          </div>
                          <span className="mt-1 text-[10px] text-gray-600 flex-shrink-0">{s.name}</span>
                        </a>
                      ) : (
                        <div className="flex flex-col items-center w-full">
                          <div
                            className="w-full flex items-center justify-center p-4 flex-shrink-0"
                            style={{
                              height: '80px',
                              backgroundColor: '#ffffff',
                              borderRadius: '8px',
                              boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                            }}
                          >
                            <img src={s.logo_url} alt={s.name} className="max-w-full max-h-[48px] w-auto h-auto object-contain" />
                          </div>
                          <span className="mt-1 text-[10px] text-gray-600 flex-shrink-0">{s.name}</span>
                        </div>
                      )}
                    </>
                  ) : s.url ? (
                    <a href={s.url} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center w-full">
                      <div
                        className="w-full flex items-center justify-center p-4 flex-shrink-0"
                        style={{
                          height: '80px',
                          backgroundColor: '#ffffff',
                          borderRadius: '8px',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                        }}
                      >
                        <span className="text-xs font-bold text-center" style={{ color: colors.warmGray }}>{s.name}</span>
                      </div>
                    </a>
                  ) : (
                    <div
                      className="w-full flex items-center justify-center p-4 flex-shrink-0"
                      style={{
                        height: '80px',
                        backgroundColor: '#ffffff',
                        borderRadius: '8px',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                      }}
                    >
                      <span className="text-xs font-bold text-center" style={{ color: colors.warmGray }}>{s.name}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* 後援一覧 */}
          <div className="mb-0 pt-12" style={{ borderTop: '1px solid rgba(74,69,67,0.15)' }}>
            <div className="text-center mb-8">
              <span
                className="inline-block px-4 py-1.5 text-xs font-bold tracking-widest uppercase mb-4"
                style={{
                  backgroundColor: `${colors.coralLight}40`,
                  color: colors.coral,
                  borderRadius: '100px',
                }}
              >
                Supporters
              </span>
              <h3 className="text-2xl font-black" style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}>
                後援
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 items-stretch">
              {sortByDisplayOrder(supporters).map((s) => (
                <div key={s.id} className="flex flex-col items-center p-2">
                  {s.logo_url ? (
                    <>
                      {s.url ? (
                        <a href={s.url} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center w-full">
                          <div
                            className="w-full flex items-center justify-center p-4 flex-shrink-0"
                            style={{
                              height: '140px',
                              backgroundColor: '#ffffff',
                              borderRadius: '12px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                            }}
                          >
                            <img src={s.logo_url} alt={s.name} className="max-w-full max-h-[108px] w-auto h-auto object-contain" />
                          </div>
                          <span className="mt-2 text-sm text-gray-600 flex-shrink-0 text-center">{s.name}</span>
                        </a>
                      ) : (
                        <div className="flex flex-col items-center w-full">
                          <div
                            className="w-full flex items-center justify-center p-4 flex-shrink-0"
                            style={{
                              height: '140px',
                              backgroundColor: '#ffffff',
                              borderRadius: '12px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                            }}
                          >
                            <img src={s.logo_url} alt={s.name} className="max-w-full max-h-[108px] w-auto h-auto object-contain" />
                          </div>
                          <span className="mt-2 text-sm text-gray-600 flex-shrink-0 text-center">{s.name}</span>
                        </div>
                      )}
                    </>
                  ) : s.url ? (
                    <a href={s.url} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center w-full">
                      <div
                        className="w-full flex items-center justify-center p-4 flex-shrink-0"
                        style={{
                          height: '140px',
                          backgroundColor: '#ffffff',
                          borderRadius: '12px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        }}
                      >
                        <span className="text-base font-bold text-center" style={{ color: colors.warmGray }}>{s.name}</span>
                      </div>
                    </a>
                  ) : (
                    <div
                      className="w-full flex items-center justify-center p-4 flex-shrink-0"
                      style={{
                        height: '140px',
                        backgroundColor: '#ffffff',
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      }}
                    >
                      <span className="text-base font-bold text-center" style={{ color: colors.warmGray }}>{s.name}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          </div>
          {/* 企業一覧エリアここまで */}
        </div>
      </section>

      </div>
      {/* ========== 協賛ブロック v2 ここまで ========== */}

      {/* -------------------------------------------------
          Executive Committee（実行委員）
      ------------------------------------------------- */}
      <section
        id="committee"
        data-animate
        className="py-28 bg-white relative overflow-hidden"
      >
        <div
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${
            isVisible['committee'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-16">
            <span
              className="inline-block px-4 py-1.5 text-xs font-bold tracking-widest uppercase mb-6"
              style={{
                backgroundColor: `${colors.coralLight}40`,
                color: colors.coral,
                borderRadius: '100px',
              }}
            >
              Committee
            </span>
            <h2
              className="text-5xl sm:text-6xl font-black mb-6"
              style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
            >
              実行委員
            </h2>
          </div>

          <div className="p-8 sm:p-10 mb-8" style={{ backgroundColor: colors.cream, borderRadius: '20px' }}>
            <div className="text-sm font-bold mb-4 tracking-wide" style={{ color: colors.coral }}>
              日本女性財団 国際女性デー実行委員会
            </div>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              {['対馬ルリ子（委員長）', '中島高英（事務局長）', '赤堀陽子', '石井大介', '岩野優子', '江川信吾', '佐々木恵美子', '田川久美', '寺倉純子', '西田利香', '羽山暁子', '村上裕子', '山名群'].map((name, i) => (
                <span
                  key={i}
                  className="inline-block px-4 py-2 text-base font-medium bg-white shadow-sm"
                  style={{ color: colors.warmGray, borderRadius: '10px', border: `1px solid ${colors.coralLight}` }}
                >
                  {name}
                </span>
              ))}
            </div>
          </div>

          <div className="p-6 sm:p-8" style={{ backgroundColor: `${colors.mintLight}40`, borderRadius: '16px' }}>
            <div className="text-sm font-bold mb-3 tracking-wide" style={{ color: colors.mint }}>
              特別サポートメンバー
            </div>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              {['磯香奈子', 'コミンズ・リオ', '洲河美貴', '秦純子'].map((name, i) => (
                <span
                  key={i}
                  className="inline-block px-4 py-2 text-base font-medium bg-white shadow-sm"
                  style={{ color: colors.warmGray, borderRadius: '10px', border: `1px solid ${colors.mintLight}` }}
                >
                  {name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* -------------------------------------------------
          Organizer / contact
      ------------------------------------------------- */}
      <section className="py-28 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span
              className="inline-block px-4 py-1.5 text-xs font-bold tracking-widest uppercase mb-6"
              style={{
                backgroundColor: `${colors.coralLight}40`,
                color: colors.coral,
                borderRadius: '100px',
              }}
            >
              Contact
            </span>
            <h2
              className="text-5xl sm:text-6xl font-black mb-6"
              style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
            >
              主催・お問い合わせ
            </h2>
          </div>

          <div
            className="bg-white p-10 sm:p-14 shadow-lg"
            style={{ borderRadius: '32px', border: '1px solid #f0f0f0' }}
          >
            <div className="text-center mb-10">
              <h3 className="text-4xl font-black mb-3" style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}>
                日本女性財団
              </h3>
              <p className="text-xl text-gray-500">国際女性デー実行委員会</p>
            </div>

            <div className="flex items-center justify-center">
              <button
                onClick={() => setShowContactForm(true)}
                className="flex items-center gap-4 px-6 py-3 bg-[#7ECEC1] text-white font-medium rounded-xl hover:bg-[#6ebdb0] transition-colors cursor-pointer shadow-sm"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl">
                  <i className="ri-mail-send-line text-2xl text-white"></i>
                </div>
                <span className="text-xl font-medium">お問い合わせフォーム</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* -------------------------------------------------
          Flyer（チラシ）
      ------------------------------------------------- */}
      <section className="py-20 relative overflow-hidden" style={{ backgroundColor: colors.cream }}>
        <div className="absolute inset-0 opacity-40">
          <div className="absolute top-0 left-1/4 w-72 h-72 rounded-full" style={{ backgroundColor: colors.mintLight, filter: 'blur(100px)' }}></div>
          <div className="absolute bottom-0 right-1/4 w-72 h-72 rounded-full" style={{ backgroundColor: colors.coralLight, filter: 'blur(100px)' }}></div>
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <span
              className="inline-block px-4 py-1.5 text-xs font-bold tracking-widest uppercase mb-4"
              style={{ backgroundColor: `${colors.mintLight}80`, color: colors.mint, borderRadius: '100px' }}
            >
              Flyer
            </span>
            <h3 className="text-2xl font-black" style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}>
              フライヤー
            </h3>
          </div>
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {flyers.map((flyer, index) => (
              <div key={index} className="relative group">
                <div
                  className="absolute -bottom-2 -right-2 w-full h-full rounded-2xl"
                  style={{ backgroundColor: index === 0 ? colors.mintLight : colors.coralLight }}
                ></div>
                <div
                  className="relative bg-white p-4 rounded-2xl shadow-lg transition-transform group-hover:-translate-y-1 cursor-pointer"
                  onClick={() => openFlyerLightbox(index)}
                >
                  <div className="aspect-[3/4] rounded-xl overflow-hidden bg-gray-100">
                    <img
                      src={flyer.src}
                      alt={flyer.alt}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <p className="text-center text-sm font-bold mt-4 flex items-center justify-center gap-2" style={{ color: colors.warmGray }}>
                    {flyer.label}
                    <i className="ri-zoom-in-line text-base opacity-60"></i>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* -------------------------------------------------
          Flyer lightbox
      ------------------------------------------------- */}
      {flyerLightboxOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
          onClick={() => setFlyerLightboxOpen(false)}
        >
          <button
            onClick={() => setFlyerLightboxOpen(false)}
            className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors z-20"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setFlyerLightboxIndex((i) => (i === 0 ? 1 : i - 1)); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors z-20"
          >
            <i className="ri-arrow-left-s-line text-3xl"></i>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setFlyerLightboxIndex((i) => (i === 1 ? 0 : i + 1)); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors z-20"
          >
            <i className="ri-arrow-right-s-line text-3xl"></i>
          </button>
          <div
            className="relative max-w-4xl max-h-[90vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={flyers[flyerLightboxIndex].src}
              alt={flyers[flyerLightboxIndex].alt}
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
            <p className="mt-4 text-white font-bold text-lg">{flyers[flyerLightboxIndex].label}</p>
            <p className="text-white/70 text-sm mt-1">
              {flyerLightboxIndex + 1} / {flyers.length}
            </p>
          </div>
        </div>
      )}

      {/* -------------------------------------------------
          Final CTA
      ------------------------------------------------- */}
      <section className="py-32 relative overflow-hidden" style={{ backgroundColor: colors.coral }}>
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-64 h-64 rounded-full opacity-20" style={{ backgroundColor: 'white', filter: 'blur(80px)' }}></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 rounded-full opacity-15" style={{ backgroundColor: 'white', filter: 'blur(100px)' }}></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white mb-8 leading-tight" style={{ fontFamily: "'Shippori Mincho', serif" }}>
            あなたの参加が<br />
            未来を変える
          </h2>
          <p className="text-2xl text-white/90 mb-4">学び、体験し、つながる。そして社会を変える一歩を。</p>
          <p className="text-xl text-white/70 mb-14">2026年3月14日（土）、TODAホールでお会いしましょう</p>

          <a
            href="https://peatix.com/event/4805808/"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-4 px-8 py-4 text-2xl font-bold whitespace-nowrap cursor-pointer rounded-xl bg-white text-[#7ECEC1] hover:bg-gray-100 transition-colors"
          >
            <span>Peatixで今すぐ申し込む</span>
            <i className="ri-arrow-right-line text-3xl group-hover:translate-x-2 transition-transform"></i>
          </a>

          <p className="mt-10 text-base text-white/60">※ Peatixの外部サイトに移動します</p>
        </div>
      </section>

      <Footer onContactClick={() => setShowContactForm(true)} />

      {/* -------------------------------------------------
          Sponsor inquiry modal
      ------------------------------------------------- */}
      {showSponsorForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="relative w-full max-w-lg bg-white p-8 shadow-2xl max-h-[90vh] overflow-y-auto" style={{ borderRadius: '24px' }}>
            <button
              onClick={() => setShowSponsorForm(false)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl text-gray-600"></i>
            </button>

            <h3 className="text-3xl font-black mb-6" style={{ color: colors.warmGray }}>
              協賛のお問い合わせ
            </h3>

            {formSuccess === 'sponsor' ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: colors.mintLight, borderRadius: '50%' }}>
                  <i className="ri-check-line text-3xl" style={{ color: colors.mint }}></i>
                </div>
                <p className="text-xl font-bold" style={{ color: colors.warmGray }}>
                  送信完了しました
                </p>
                <p className="text-gray-500 mt-2 text-base">
                  送信内容の確認メールをお送りしました。担当者より折り返しご連絡いたします。
                </p>
              </div>
            ) : (
              <form id="sponsor-form" onSubmit={handleSponsorFormSubmit} className="space-y-5">
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">
                    会社名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="会社名"
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-base"
                    placeholder="株式会社〇〇"
                  />
                </div>
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="お名前"
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-base"
                    placeholder="山田 太郎"
                  />
                </div>
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="メールアドレス"
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-base"
                    placeholder="example@company.com"
                  />
                </div>
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">電話番号</label>
                  <input
                    type="tel"
                    name="電話番号"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-base"
                    placeholder="03-1234-5678"
                  />
                </div>
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">ご検討中のプラン</label>
                  <select name="ご検討中のプラン" className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-base">
                    <option value="">選択してください</option>
                    <option value="ダイヤモンド">ダイヤモンド（30万円〜）</option>
                    <option value="プラチナ">プラチナ（20万円）</option>
                    <option value="ゴールド">ゴールド（10万円）</option>
                    <option value="ルビー">ルビー（物品協賛）</option>
                    <option value="未定">未定・相談したい</option>
                  </select>
                </div>
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">お問い合わせ内容</label>
                  <textarea
                    name="お問い合わせ内容"
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-base resize-none"
                    placeholder="ご質問やご要望がございましたらご記入ください"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={formSubmitting}
                  className="w-full py-3 text-white font-medium rounded-xl bg-[#7ECEC1] hover:bg-[#6ebdb0] disabled:opacity-50 transition-colors whitespace-nowrap cursor-pointer text-base"
                >
                  {formSubmitting ? '送信中...' : '送信する'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* -------------------------------------------------
          Contact modal
      ------------------------------------------------- */}
      {showContactForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="relative w-full max-w-lg bg-white p-8 shadow-2xl max-h-[90vh] overflow-y-auto" style={{ borderRadius: '24px' }}>
            <button
              onClick={() => setShowContactForm(false)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl text-gray-600"></i>
            </button>

            <h3 className="text-3xl font-black mb-6" style={{ color: colors.warmGray }}>
              お問い合わせ
            </h3>

            {formSuccess === 'contact' ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: colors.mintLight, borderRadius: '50%' }}>
                  <i className="ri-check-line text-3xl" style={{ color: colors.mint }}></i>
                </div>
                <p className="text-xl font-bold" style={{ color: colors.warmGray }}>
                  送信完了しました
                </p>
                <p className="text-gray-500 mt-2 text-base">
                  送信内容の確認メールをお送りしました。担当者より折り返しご連絡いたします。
                </p>
              </div>
            ) : (
              <form id="contact-form" onSubmit={handleContactFormSubmit} className="space-y-5">
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="お名前"
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-base"
                    placeholder="山田 太郎"
                  />
                </div>
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="メールアドレス"
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-base"
                    placeholder="example@email.com"
                  />
                </div>
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">お問い合わせ種別</label>
                  <select name="お問い合わせ種別" className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-base">
                    <option value="">選択してください</option>
                    <option value="イベントについて">イベントについて</option>
                    <option value="チケットについて">チケットについて</option>
                    <option value="協賛について">協賛について</option>
                    <option value="取材・メディア">取材・メディア</option>
                    <option value="その他">その他</option>
                  </select>
                </div>
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">
                    お問い合わせ内容 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="お問い合わせ内容"
                    rows={5}
                    maxLength={500}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-base resize-none"
                    placeholder="お問い合わせ内容をご記入ください"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={formSubmitting}
                  className="w-full py-3 text-white font-medium rounded-xl bg-[#7ECEC1] hover:bg-[#6ebdb0] disabled:opacity-50 transition-colors whitespace-nowrap cursor-pointer text-base"
                >
                  {formSubmitting ? '送信中...' : '送信する'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
